<template>
    <v-container>
        <v-tabs v-model="detailActiveTab" color="primary" class="mb-4 detail-tabs" show-arrows>
            <v-tab href="#tab-info">
                <v-icon left small>mdi-information-outline</v-icon>
                {{ $t('ticket.information.title') }}
            </v-tab>
            <v-tab v-if="showChecklistSectionEffective" href="#tab-arquivos">
                <v-icon left small>{{ detailSecondaryTabIcon }}</v-icon>
                {{ checklistTabDisplayName }}
            </v-tab>
            <v-tab href="#tab-tasks">
                <v-icon left small>mdi-format-list-numbered</v-icon>
                {{ $t('ticket.detail.tabs.tasksAndComments') }}
            </v-tab>
            <v-tab v-if="podeVerHistorico" href="#tab-historico">
                <v-icon left>mdi-history</v-icon>
                {{ $t('ticketSuporteFluxo.historicoTitulo') }}
            </v-tab>
        </v-tabs>
        <v-tabs-items v-model="detailActiveTab">
            <!-- Tab: Information and Edit Cards -->
            <v-tab-item value="tab-info">
                <div class="detail-tab-content">
                <v-row>
                    <!-- Left column: read-only information (full width when right column is empty) -->
                    <v-col cols="12" :md="hasRightColumnContent ? 6 : 12">
                <ticket-suporte-fluxo-card
                    v-if="isSuporteSistemaTicket && editedItem.id_ticket"
                    :ticket="editedItem"
                    :familias="familiasAtendimento"
                    :usuario="usuarioAtual"
                    :somente-leitura="readOnlyTicketDetail"
                    @updated="$emit('updated')"
                ></ticket-suporte-fluxo-card>
                <!-- Information Card -->
                <v-card class="pa-4 mb-4" outlined>
                    <v-card-title class="text-h6 pa-0 mb-3">
                        <v-icon left color="primary">mdi-calendar-clock</v-icon>
                        {{ $t('ticket.information.title') }}
                    </v-card-title>
                    <v-divider class="mb-3"></v-divider>
                    <v-row>
                        <v-col cols="12">
                            <div class="d-flex align-center mb-3" v-if="showEmpresa && showGenericTicketHierarchy">
                                <v-icon color="primary" class="mr-2">mdi-building</v-icon>
                                <div>
                                    <div class="text-caption grey--text">
                                        {{ $tc('company.self', 1) }}
                                    </div>
                                    <div class="text-body-1 font-weight-medium">
                                        {{ editedItem.empresa_extenso || '-' }}
                                    </div>
                                </div>
                            </div>
                            <div class="d-flex align-center mb-3" v-if="showDtInclusao">
                                <v-icon color="primary" class="mr-2">mdi-calendar</v-icon>
                                <div>
                                    <div class="text-caption grey--text">
                                        {{ $t('ticket.information.openingDate') }}
                                    </div>
                                    <div class="text-body-1 font-weight-medium">
                                        {{ editedItem.dt_inclusao_extenso }}
                                    </div>
                                </div>
                            </div>
                            <div class="d-flex align-center mb-3" v-if="showDtPrevistaFim && showGenericTicketHierarchy">
                                <v-icon color="primary" class="mr-2">mdi-calendar</v-icon>
                                <div>
                                    <div class="text-caption grey--text">
                                        {{ $t('ticket.tasks.expectedCompletionDate') }}
                                    </div>
                                    <div class="text-body-1 font-weight-medium">
                                        {{ editedItem.dt_prevista_fim_extenso ? editedItem.dt_prevista_fim_extenso : '-' }}
                                    </div>
                                </div>
                            </div>
                            <div class="d-flex align-center mb-3" v-if="editedItem.sla_manual && !showSlaManualEditEffective">
                                <v-icon color="primary" class="mr-2">mdi-timer-edit-outline</v-icon>
                                <div>
                                    <div class="text-caption grey--text">
                                        {{ $t('ticket.sla.manual') }}
                                    </div>
                                    <div class="text-body-1 font-weight-medium">
                                        {{ $t('ticket.sla.option', { n: parseInt(editedItem.sla_manual, 10) }) }}
                                    </div>
                                </div>
                            </div>
                            <div class="d-flex align-center mb-3" v-if="showDtFinalizacao && isTicketClosed && (editedItem.dt_finalizacao_extenso || editedItem.dt_finalizacao)">
                                <v-icon color="primary" class="mr-2">mdi-calendar-check</v-icon>
                                <div>
                                    <div class="text-caption grey--text">
                                        {{ $t('ticket.information.finalizationDate') }}
                                    </div>
                                    <div class="text-body-1 font-weight-medium">
                                        {{ editedItem.dt_finalizacao_extenso || editedItem.dt_finalizacao || '-' }}
                                    </div>
                                </div>
                            </div>
                            <div class="d-flex align-center mb-3" v-if="showNomeUsuarioInclusao">
                                <v-icon color="primary" class="mr-2">mdi-account</v-icon>
                                <div>
                                    <div class="text-caption grey--text">
                                        {{ $t('ticket.information.requester') }}
                                    </div>
                                    <div class="text-body-1 font-weight-medium">
                                        {{ editedItem.nome_usuario_inclusao }}
                                    </div>
                                </div>
                            </div>
                            <div class="d-flex align-center mb-3" v-if="solicitanteDisplayValue">
                                <v-icon color="primary" class="mr-2">mdi-account-outline</v-icon>
                                <div>
                                    <div class="text-caption grey--text">
                                        {{ $t('ticket.createDialog.fields.claimant') }}
                                    </div>
                                    <div class="text-body-1 font-weight-medium">
                                        {{ solicitanteDisplayValue }}
                                    </div>
                                </div>
                            </div>
                            <div class="d-flex align-center mb-3" v-if="editedItem.telefone_solicitante">
                                <v-icon color="primary" class="mr-2">mdi-phone</v-icon>
                                <div>
                                    <div class="text-caption grey--text">
                                        {{ $t('ticket.createDialog.fields.telefoneSolicitante') }}
                                    </div>
                                    <div class="text-body-1 font-weight-medium">
                                        {{ editedItem.telefone_solicitante }}
                                    </div>
                                </div>
                            </div>
                            <!-- categoria (commented out)
                            <div class="d-flex align-center mb-3" v-if="editedItem.categoria_extenso">
                                <v-icon color="primary" class="mr-2">mdi-tag</v-icon>
                                <div>
                                    <div class="text-caption grey--text">
                                        {{ $t('ticket.information.category') }}
                                    </div>
                                    <div class="text-body-1 font-weight-medium">
                                        {{ editedItem.categoria_extenso }}
                                    </div>
                                </div>
                            </div>
                            -->
                            <div class="d-flex align-center mb-3" v-if="showTipoTicket && editedItem.tipo_ticket_extenso">
                                <v-icon color="primary" class="mr-2">mdi-ticket</v-icon>
                                <div>
                                    <div class="text-caption grey--text">
                                        {{ $t('ticket.createDialog.fields.tipoTicket.label') }}
                                    </div>
                                    <div class="text-body-1 font-weight-medium">
                                        {{ editedItem.tipo_ticket_extenso }}
                                    </div>
                                </div>
                            </div>
                            <div class="d-flex align-center mb-3" v-if="isAgenciaCandidatosTicket && showAgenciaCandidatosAgenciaInfo">
                                <v-icon color="primary" class="mr-2">mdi-domain</v-icon>
                                <div>
                                    <div class="text-caption grey--text">
                                        {{ $t('ticketSolicitacaoCandidatos.fields.agencia') }}
                                    </div>
                                    <div class="text-body-1 font-weight-medium">
                                        {{ agenciaCandidatosAgenciaDisplay }}
                                    </div>
                                </div>
                            </div>
                            <div class="d-flex align-center mb-3" v-if="showTipoSolicitacao && editedItem.tipo_solicitacao_extenso">
                                <v-icon color="primary" class="mr-2">mdi-file-document-outline</v-icon>
                                <div>
                                    <div class="text-caption grey--text">
                                        {{ isSuporteSistemaTicket
                                            ? $t('ticketSuporteSistema.fields.tipoProblema')
                                            : $t('ticket.createDialog.fields.tipoSolicitacao.label') }}
                                    </div>
                                    <div class="text-body-1 font-weight-medium">
                                        {{ editedItem.tipo_solicitacao_extenso }}
                                    </div>
                                </div>
                            </div>
                            <div class="d-flex align-center mb-3" v-if="isSuporteSistemaTicket && !familiaSemModulo">
                                <v-icon color="primary" class="mr-2">mdi-view-module</v-icon>
                                <div>
                                    <div class="text-caption grey--text">
                                        {{ $t('ticketSuporteSistema.fields.modulo') }}
                                    </div>
                                    <div class="text-body-1 font-weight-medium">
                                        {{ suporteSistemaModuloDisplay }}
                                    </div>
                                </div>
                            </div>
                            <div class="d-flex align-start mb-3" v-if="isAgenciaCandidatosTicket && showAgenciaCandidatosUnidadesInfo">
                                <v-icon color="primary" class="mr-2 mt-1">mdi-office-building-outline</v-icon>
                                <div class="flex-grow-1">
                                    <div class="text-caption grey--text">
                                        {{ $t('ticket.information.businessUnits') }}
                                    </div>
                                    <div class="text-body-1 font-weight-medium">
                                        {{ agenciaCandidatosUnidadesDisplay }}
                                    </div>
                                </div>
                            </div>
                            <div class="d-flex align-start mb-3" v-if="isAgenciaCandidatosTicket && agenciaCandidatosAreasDisplay">
                                <v-icon color="primary" class="mr-2 mt-1">mdi-vector-square</v-icon>
                                <div class="flex-grow-1">
                                    <div class="text-caption grey--text">
                                        {{ $t('ticket.headers.areas') }}
                                    </div>
                                    <div class="text-body-1 font-weight-medium">
                                        {{ agenciaCandidatosAreasDisplay }}
                                    </div>
                                </div>
                            </div>
                            <div class="d-flex align-start mb-3" v-if="isAgenciaCandidatosTicket && showAgenciaCandidatosAreaApoioInfo">
                                <v-icon color="primary" class="mr-2 mt-1">mdi-hand-extended-outline</v-icon>
                                <div class="flex-grow-1">
                                    <div class="text-caption grey--text">
                                        {{ $t('ticket.information.areaApoio') }}
                                    </div>
                                    <div class="text-body-1 font-weight-medium">
                                        {{ agenciaCandidatosAreaApoioDisplay }}
                                    </div>
                                </div>
                            </div>
                            <div class="d-flex align-center mb-3" v-if="showGenericTicketHierarchy && showFamiliaAtendimento && editedItem.familia_atendimento_extenso">
                                <v-icon color="primary" class="mr-2">mdi-folder-outline</v-icon>
                                <div>
                                    <div class="text-caption grey--text">
                                        {{ $t('ticket.createDialog.fields.familiaAtendimento.label') }}
                                    </div>
                                    <div class="text-body-1 font-weight-medium">
                                        {{ editedItem.familia_atendimento_extenso }}
                                    </div>
                                </div>
                            </div>
                            <div class="d-flex align-center mb-3" v-if="showGenericTicketHierarchy && showTipoAtendimento && editedItem.tipo_atendimento_extenso">
                                <v-icon color="primary" class="mr-2">mdi-format-list-bulleted</v-icon>
                                <div>
                                    <div class="text-caption grey--text">
                                        {{ $t('ticket.createDialog.fields.tipoAtendimento.label') }}
                                    </div>
                                    <div class="text-body-1 font-weight-medium">
                                        {{ editedItem.tipo_atendimento_extenso }}
                                    </div>
                                </div>
                            </div>
                            <div class="d-flex align-center mb-3" v-if="showGenericTicketHierarchy && showServico && editedItem.servico_extenso">
                                <v-icon color="primary" class="mr-2">mdi-cog-outline</v-icon>
                                <div>
                                    <div class="text-caption grey--text">
                                        {{ $t('ticket.createDialog.fields.servico.label') }}
                                    </div>
                                    <div class="text-body-1 font-weight-medium">
                                        {{ editedItem.servico_extenso }}
                                    </div>
                                </div>
                            </div>
                            <div class="d-flex align-center mb-3" v-if="showAssignedUserEffective">
                                <v-icon color="primary" class="mr-2">mdi-account</v-icon>
                                <div>
                                    <div class="text-caption grey--text">
                                        {{ $t('ticket.usuario.assigned') }}
                                    </div>
                                    <div class="text-body-1 font-weight-medium">
                                        {{ assigneesDisplayLabel }}
                                    </div>
                                </div>
                            </div>
                            <div class="d-flex align-center mb-3" v-if="showTitulo">
                                <v-icon class="mr-2" color="primary" size="24">mdi-file-document</v-icon>
                                <div>
                                    <div class="text-caption grey--text">
                                        {{ $t('ticket.titleField.label') }}
                                    </div>
                                    <div class="text-body-1 font-weight-medium">
                                        {{ editedItem.titulo || '-' }}
                                    </div>
                                </div>
                            </div>

                            <div class="d-flex align-center mb-3" v-if="showGenericTicketHierarchy && showVehicleEffective && editedItem.veiculos_associados && editedItem.veiculos_associados.length > 0">
                                <v-icon class="mr-2" color="primary" size="24">mdi-car</v-icon>
                                <div>
                                    <div class="text-caption grey--text">
                                        {{ $tc('vehicle.self', 1) }}
                                    </div>
                                    <div class="text-body-1 font-weight-medium">
                                        {{ veiculosDisplayText }}
                                    </div>
                                </div>
                            </div>
                            <div class="d-flex align-start mb-3" v-if="showGenericTicketHierarchy && showEquipmentEffective && editedItem.equipamentos_associados && editedItem.equipamentos_associados.length > 0">
                                <v-icon class="mr-2 mt-1" color="primary" size="24">mdi-cog</v-icon>
                                <div class="flex-grow-1">
                                    <div class="text-caption grey--text mb-1">
                                        {{ $tc('equipment.self', 2) }}
                                    </div>
                                    <div
                                        v-for="(eq, idx) in equipamentosComTipo"
                                        :key="'eq-' + (eq.id_equipamento != null ? eq.id_equipamento : idx)"
                                        class="text-body-1 font-weight-medium mb-1"
                                    >
                                        {{ eq.displayText }}
                                        <span v-if="eq.typeName" class="text-caption grey--text font-weight-regular">
                                            ({{ $t('ticket.detail.equipmentType') }}: {{ eq.typeName }})
                                        </span>
                                    </div>
                                </div>
                            </div>
                            <div
                                class="d-flex align-start mb-3"
                                v-if="showGenericTicketHierarchy && showEquipmentEffective && editedItem.equipamentos_do_kit && editedItem.equipamentos_do_kit.length > 0"
                            >
                                <v-icon class="mr-2 mt-1" color="primary" size="24">mdi-package-variant</v-icon>
                                <div class="flex-grow-1">
                                    <div class="text-caption grey--text mb-1">
                                        {{ $t('ticket.detail.kitLineItems') }}
                                    </div>
                                    <div
                                        v-for="(row, kidx) in equipamentosKitLinhas"
                                        :key="'kit-eq-' + row.id_kit + '-' + (row.id_kit_equipamento != null ? row.id_kit_equipamento : row.id_equipamento) + '-' + kidx"
                                        class="text-body-1 font-weight-medium mb-1"
                                    >
                                        <span class="text-caption grey--text">{{ row.kitLabel }}</span>
                                        {{ row.itemText }}
                                    </div>
                                </div>
                            </div>
                            <div class="d-flex align-center mb-3" v-if="showAgenciaCandidatosUnifiedStatusReadonly">
                                <v-icon class="mr-2" color="primary" size="24">mdi-view-list</v-icon>
                                <div>
                                    <div class="text-caption grey--text">{{ $t('ticket.status.label') }}</div>
                                    <div class="text-body-1 font-weight-medium">
                                        {{ agenciaCandidatosStatusExtenso || '-' }}
                                    </div>
                                </div>
                            </div>
                            <div class="d-flex align-center mb-3" v-if="!isAgenciaCandidatosTicket && showStatusEffective">
                                <v-icon class="mr-2" color="primary" size="24">mdi-view-list</v-icon>
                                <div>
                                    <div class="text-caption grey--text">{{ $t('ticket.status.label') }}</div>
                                    <div class="text-body-1 font-weight-medium">
                                        {{ editedItem.status_extenso || '-' }}
                                    </div>
                                </div>
                            </div>
                            <div class="d-flex align-center mb-3" v-if="!isAgenciaCandidatosTicket && showStatusAtribuidoEffective && (editedItem.status_atribuido_extenso || editedItem.id_status_atribuido)">
                                <v-icon class="mr-2" color="primary" size="24">mdi-account-wrench</v-icon>
                                <div>
                                    <div class="text-caption grey--text">{{ $t('ticket.status.statusAtribuido') }}</div>
                                    <div class="text-body-1 font-weight-medium">
                                        {{ editedItem.status_atribuido_extenso || '-' }}
                                    </div>
                                </div>
                            </div>
                            <div class="d-flex align-center mb-3" v-if="!isAgenciaCandidatosTicket && showStatusDispatcherEffective && (editedItem.status_dispatcher_extenso || editedItem.id_status_dispatcher)">
                                <v-icon class="mr-2" color="primary" size="24">mdi-account-supervisor</v-icon>
                                <div>
                                    <div class="text-caption grey--text">{{ $t('ticket.status.statusDispatcher') }}</div>
                                    <div class="text-body-1 font-weight-medium">
                                        {{ editedItem.status_dispatcher_extenso || '-' }}
                                    </div>
                                </div>
                            </div>
                            <div class="d-flex align-center mb-3" v-if="showGenericTicketHierarchy && showScheduleEffective">
                                <v-icon class="mr-2" color="primary" size="24">mdi-calendar</v-icon>
                                <div>
                                    <div class="text-caption grey--text">
                                        {{ $t('ticket.schedule.label') }}
                                    </div>
                                    <div class="text-body-1 font-weight-medium">
                                        {{ editedItem.dt_agendamento_extenso || '-' }}
                                    </div>
                                </div>
                            </div>
                            <div class="d-flex align-start" v-if="showCorpo">
                                <v-icon class="mr-2" color="primary" size="24">mdi-text-box</v-icon>
                                <div class="flex-grow-1">
                                    <div class="text-caption grey--text">
                                        {{ $t('ticket.description.label') }}
                                    </div>
                                    <div class="text-body-2 font-weight-medium primary--text mb-2">
                                        {{ editedItem.cod_ticket || ('#' + editedItem.id_ticket) }}
                                    </div>
                                    <div
                                        class="text-body-2 mb-2"
                                        v-if="editedItem.linear_url || editedItem.linear_identifier"
                                    >
                                        <a
                                            v-if="editedItem.linear_url"
                                            :href="editedItem.linear_url"
                                            target="_blank"
                                            rel="noopener noreferrer"
                                        >Linear: {{ editedItem.linear_identifier || 'issue' }}</a>
                                        <span v-else>Linear: {{ editedItem.linear_identifier }}</span>
                                    </div>
                                    <div class="text-body-1 font-weight-medium ticket-detail-corpo">
                                        {{ editedItem.corpo || '-' }}
                                    </div>
                                </div>
                            </div>
                            <div class="d-flex align-center mb-3" v-if="showEnderecoEffective && editedItem.endereco">
                                <v-icon class="mr-2" color="primary" size="24">mdi-map-marker</v-icon>
                                <div>
                                    <div class="text-caption grey--text">
                                        {{ $t('ticket.createDialog.fields.localEmpresa') }}
                                    </div>
                                    <div class="text-body-1 font-weight-medium">
                                        {{ editedItem.endereco }}
                                    </div>
                                </div>
                            </div>
                            <div class="d-flex align-center mb-3" v-if="showGenericTicketHierarchy && showChecklistEffective && editedItem.servico_nomes_checklists">
                                <v-icon class="mr-2" color="primary" size="24">mdi-clipboard-list</v-icon>
                                <div>
                                    <div class="text-caption grey--text">
                                        {{ $t('ticket.checklist.serviceChecklists') || 'Checklists do serviço' }}
                                    </div>
                                    <div class="text-body-1 font-weight-medium">
                                        {{ editedItem.servico_nomes_checklists }}
                                    </div>
                                </div>
                            </div>
                        </v-col>
                    </v-row>
                </v-card>
                    </v-col>
                    <!-- Right column: editable selects and actions (only rendered when there is content) -->
                    <v-col v-if="hasRightColumnContent" cols="12" md="6">
                <!-- Pendente: criado pelo usuário, aguarda a triagem para virar ticket (Aberto) ao salvar -->
                <v-alert
                    v-if="isTicketPendente && showSelectionTreeEditEffective"
                    type="warning"
                    outlined
                    dense
                    class="mb-4"
                >
                    <div class="font-weight-medium">{{ $t('ticket.pendente.title') }}</div>
                    <div class="text-body-2">{{ $t('ticket.pendente.hint') }}</div>
                    <div v-if="camposPendentesLabels.length" class="text-body-2 mt-1">
                        {{ $t('ticket.pendente.missing') }} {{ camposPendentesLabels.join(', ') }}
                    </div>
                </v-alert>
                <!-- Selection tree (Tipo Ticket, Familia, Tipo Atendimento, Serviço) - editable when showSelectionTreeEdit -->
                <v-card class="pa-4 mb-4" outlined v-if="showGenericTicketHierarchy && showSelectionTreeEditEffective && editedItem.id_ticket">
                    <template>
                    <v-card-title class="text-h6 pa-0 mb-3">
                        <v-icon left color="primary">mdi-tag-multiple</v-icon>
                        {{ $t('ticket.createDialog.fields.selectionTreeTitle') }}
                    </v-card-title>
                    <v-divider class="mb-3"></v-divider>
                    <v-row>
                        <v-col cols="12">
                            <v-select
                                :value="editedItem.id_tipo_ticket"
                                @input="$emit('update:tipoTicket', $event)"
                                :items="tiposTickets"
                                item-text="nome_tipo_ticket"
                                item-value="id_tipo_ticket"
                                :label="$t('ticket.createDialog.fields.tipoTicket.label')"
                                clearable
                                dense
                                :disabled="isTicketClosed"
                            ></v-select>
                        </v-col>
                    </v-row>
                    <v-row>
                        <v-col cols="12">
                            <v-select
                                :value="editedItem.id_tipo_solicitacao"
                                @input="$emit('update:tipoSolicitacao', $event)"
                                :items="tiposSolicitacao"
                                :item-text="rotuloSolicitacao"
                                item-value="id_tipo_solicitacao"
                                :label="$t('ticket.createDialog.fields.tipoSolicitacao.label')"
                                clearable
                                dense
                                :disabled="isTicketClosed"
                            ></v-select>
                        </v-col>
                    </v-row>
                    <v-row>
                        <v-col cols="12">
                            <v-select
                                :value="editedItem.id_familia_atendimento"
                                @input="$emit('update:familia', $event)"
                                :items="familiasAtendimento"
                                :item-text="rotuloFamilia"
                                item-value="id_familia_atendimento"
                                :label="$t('ticket.createDialog.fields.familiaAtendimento.label')"
                                clearable
                                dense
                                :disabled="isTicketClosed"
                            ></v-select>
                        </v-col>
                    </v-row>
                    <v-row>
                        <v-col cols="12">
                            <v-select
                                :value="editedItem.id_tipo_atendimento"
                                @input="$emit('update:tipoAtendimento', $event)"
                                :items="tiposAtendimento"
                                :item-text="rotuloAtendimento"
                                item-value="id_tipo_atendimento"
                                :label="$t('ticket.createDialog.fields.tipoAtendimento.label')"
                                :disabled="!editedItem.id_familia_atendimento || isTicketClosed"
                                clearable
                                dense
                            ></v-select>
                        </v-col>
                    </v-row>
                    <v-row>
                        <v-col cols="12">
                            <v-select
                                :value="editedItem.id_servico"
                                @input="$emit('update:servico', $event)"
                                :items="servicos"
                                :item-text="rotuloServico"
                                item-value="id_servico"
                                :label="$t('ticket.createDialog.fields.servico.label')"
                                :disabled="!editedItem.id_tipo_atendimento || isTicketClosed"
                                clearable
                                dense
                            ></v-select>
                        </v-col>
                    </v-row>
                    <v-row v-if="editedItem.id_ticket">
                        <v-col cols="12">
                            <v-select
                                :value="teamSelectValue"
                                @input="$emit('update:team', $event)"
                                :items="equipes"
                                item-text="nome_equipe"
                                item-value="id_equipe"
                                :label="$t('ticket.team.select')"
                                dense
                                clearable
                                :disabled="isTicketClosed"
                                :key="'eq-' + editedItem.id_ticket + '-' + (teamSelectValue != null ? teamSelectValue : 'none')"
                            />
                        </v-col>
                    </v-row>
                    </template>
                </v-card>

                <!-- Classificação de Suporte sistema / Solicitação de candidatos (definida na edição, não na criação) -->
                <v-card class="pa-4 mb-4" outlined v-if="!showGenericTicketHierarchy && showSelectionTreeEditEffective && editedItem.id_ticket">
                    <v-card-title class="text-h6 pa-0 mb-3">
                        <v-icon left color="primary">mdi-tag-multiple</v-icon>
                        {{ $t('ticket.createDialog.fields.selectionTreeTitle') }}
                    </v-card-title>
                    <v-divider class="mb-3"></v-divider>
                    <!-- Escolher Suporte Sistema troca o bloco: o tipo continua aqui para poder ser mudado. -->
                    <v-row v-if="isSuporteSistemaTicket">
                        <v-col cols="12">
                            <v-select
                                :value="editedItem.id_tipo_ticket"
                                @input="$emit('update:tipoTicket', $event)"
                                :items="tiposTickets"
                                item-text="nome_tipo_ticket"
                                item-value="id_tipo_ticket"
                                :label="$t('ticket.createDialog.fields.tipoTicket.label')"
                                clearable
                                dense
                                :disabled="isTicketClosed"
                            ></v-select>
                        </v-col>
                    </v-row>
                    <v-row>
                        <v-col cols="12">
                            <v-select
                                :value="editedItem.id_tipo_solicitacao"
                                @input="$emit('update:tipoSolicitacao', $event)"
                                :items="tiposSolicitacao"
                                :item-text="rotuloSolicitacao"
                                item-value="id_tipo_solicitacao"
                                :label="isSuporteSistemaTicket
                                    ? $t('ticketSuporteSistema.fields.tipoProblema')
                                    : $t('ticket.createDialog.fields.tipoSolicitacao.label')"
                                clearable
                                dense
                                :disabled="isTicketClosed"
                            ></v-select>
                        </v-col>
                    </v-row>
                    <template v-if="isSuporteSistemaTicket">
                        <v-row>
                            <v-col cols="12">
                                <v-select
                                    :value="editedItem.id_familia_atendimento"
                                    @input="$emit('update:familia', $event)"
                                    :items="familiasAtendimento"
                                    :item-text="rotuloFamilia"
                                    item-value="id_familia_atendimento"
                                    :label="$t('ticketSuporteFluxo.familia')"
                                    clearable
                                    dense
                                    :disabled="isTicketClosed"
                                ></v-select>
                            </v-col>
                        </v-row>
                        <v-row>
                            <v-col cols="12">
                                <v-select
                                    :value="editedItem.id_tipo_atendimento"
                                    @input="$emit('update:tipoAtendimento', $event)"
                                    :items="tiposAtendimento"
                                    :item-text="rotuloAtendimento"
                                    item-value="id_tipo_atendimento"
                                    :label="$t('ticket.createDialog.fields.tipoAtendimento.label')"
                                    :disabled="!editedItem.id_familia_atendimento || isTicketClosed"
                                    clearable
                                    dense
                                ></v-select>
                            </v-col>
                        </v-row>
                        <v-row>
                            <v-col cols="12">
                                <v-select
                                    :value="editedItem.id_servico"
                                    @input="$emit('update:servico', $event)"
                                    :items="servicos"
                                    :item-text="rotuloServico"
                                    item-value="id_servico"
                                    :label="$t('ticket.createDialog.fields.servico.label')"
                                    :disabled="!editedItem.id_tipo_atendimento || isTicketClosed"
                                    clearable
                                    dense
                                ></v-select>
                            </v-col>
                        </v-row>
                    </template>
                    <v-row v-if="isSuporteSistemaTicket && familiaSoftware">
                        <v-col cols="12">
                            <v-autocomplete
                                :value="moduloSistemaSelectValue"
                                @input="$emit('update:moduloSistema', $event)"
                                :items="modulosSistemaComGrupo"
                                item-text="label"
                                item-value="id_modulo_sistema"
                                :label="$t('ticketSuporteSistema.fields.modulo')"
                                clearable
                                dense
                                :disabled="isTicketClosed"
                            ></v-autocomplete>
                        </v-col>
                    </v-row>
                    <v-row v-if="editedItem.id_ticket">
                        <v-col cols="12">
                            <v-select
                                :value="teamSelectValue"
                                @input="$emit('update:team', $event)"
                                :items="equipes"
                                item-text="nome_equipe"
                                item-value="id_equipe"
                                :label="$t('ticket.team.select')"
                                dense
                                clearable
                                :disabled="isTicketClosed"
                                :key="'eq-' + editedItem.id_ticket + '-' + (teamSelectValue != null ? teamSelectValue : 'none')"
                            />
                        </v-col>
                    </v-row>
                </v-card>

                <!-- Compartilhar com (somente visualização) -->
                <v-card class="pa-4 mb-4" outlined v-if="showSelectionTreeEditEffective && editedItem.id_ticket">
                    <v-card-title class="text-h6 pa-0 mb-3">
                        <v-icon left color="primary">mdi-account-eye</v-icon>
                        {{ $t('ticket.createDialog.fields.shareWithViewOnly') }}
                    </v-card-title>
                    <v-divider class="mb-3"></v-divider>
                    <multipla-escolha
                        :key="'vis-' + editedItem.id_ticket"
                        lazy
                        get-url="getUsuarioslazy"
                        text="nome_usuario"
                        value="id_usuario"
                        :items="visualizadoresIniciais"
                        :label="$t('ticket.createDialog.fields.shareWithViewOnly')"
                        :selected-items="visualizadoresSelectValue"
                        :disabled="isTicketClosed"
                        @update:selectedItems="$emit('update:visualizadores', $event || [])"
                    ></multipla-escolha>
                </v-card>

                <!-- Status Change Card (legacy id_status) -->
                <v-card class="pa-4 mb-4" outlined v-if="showStatusChangeEffective">
                    <v-card-title class="text-h6 pa-0 mb-3">
                        <v-icon left color="primary">mdi-update</v-icon>
                        {{ $t('ticket.status.change') }}
                    </v-card-title>
                    <v-divider class="mb-3"></v-divider>
                    <v-row>
                        <v-col cols="12">
                            <v-select 
                                :value="editedItem.id_status" 
                                @input="$emit('update:status', $event)"
                                :items="status"
                                :item-text="rotuloStatus"
                                item-value="id_status_ticket"
                                :label="$t('ticket.status.select')"
                                :disabled="isTicketClosed"
                                dense  
                                hide-details 
                            />
                        </v-col>
                    </v-row>
                </v-card>

                <!-- Status (attributed user / technician) -->
                <!-- Status (atribuido) change: display current + button to open dialog -->
                <v-card class="pa-4 mb-4" outlined v-if="showStatusAtribuidoChangeEffective && (!isAgenciaCandidatosTicket || !showStatusDispatcherChangeEffective)">
                    <v-card-title class="text-h6 pa-0 mb-3">
                        <v-icon left color="primary">mdi-account-wrench</v-icon>
                        {{ $t('ticket.status.changeAtribuido') }}
                    </v-card-title>
                    <v-divider class="mb-3"></v-divider>
                    <v-row>
                        <v-col cols="12">
                            <div class="text-body-1 font-weight-medium mb-2">
                                {{ editedItem.status_atribuido_extenso || $t('ticket.status.notSet') || '-' }}
                            </div>
                            <v-btn
                                color="primary"
                                outlined
                                small
                                :disabled="isTicketClosed || isTicketPendente"
                                @click="openChangeStatusAtribuidoDialog"
                            >
                                <v-icon left small>mdi-update</v-icon>
                                {{ $t('ticket.tasks.changeStatus') }}
                            </v-btn>
                        </v-col>
                    </v-row>
                </v-card>

                <!-- Change task status dialog (technician: new status, dt inicio/fim, observations) -->
                <v-dialog v-model="changeStatusAtribuidoDialog" max-width="500" persistent>
                    <v-card>
                        <v-card-title class="text-h6">
                            <v-icon left color="primary">mdi-account-wrench</v-icon>
                            {{ $t('ticket.tasks.changeStatusDialogTitle') }}
                        </v-card-title>
                        <v-divider></v-divider>
                        <v-card-text class="pt-4">
                            <v-select
                                v-model="changeStatusDialogIdStatus"
                                :items="status"
                                :item-text="rotuloStatus"
                                item-value="id_status_ticket"
                                :label="$t('ticket.status.selectAtribuido') + ' *'"
                                dense
                                outlined
                                clearable
                                :disabled="isTicketClosed"
                                :rules="[v => v != null && v !== '' || $t('validation.required')]"
                            />
                            <v-datetime-picker
                                v-model="changeStatusDialogDtInicio"
                                :label="$t('ticket.tasks.dateTimeInitial') + ' *'"
                                class="mt-3"
                                :time-picker-props="checklistTimeProps"
                                :text-field-props="checklistTextFieldProps"
                                time-format="HH:mm:ss"
                                date-format="dd/MM/yyyy"
                                :clear-text="$t('datetime.close')"
                                :disabled="isTicketClosed"
                            >
                                <template slot="dateIcon">
                                    <v-icon>mdi-calendar</v-icon>
                                </template>
                                <template slot="timeIcon">
                                    <v-icon>mdi-clock</v-icon>
                                </template>
                            </v-datetime-picker>
                            <v-datetime-picker
                                v-model="changeStatusDialogDtFim"
                                :label="$t('ticket.tasks.dateTimeFinal') + ' *'"
                                class="mt-3"
                                :time-picker-props="checklistTimeProps"
                                :text-field-props="checklistTextFieldProps"
                                :date-picker-props="changeStatusDialogDtFimMinDateProps"
                                time-format="HH:mm:ss"
                                date-format="dd/MM/yyyy"
                                :clear-text="$t('datetime.close')"
                                :disabled="isTicketClosed"
                            >
                                <template slot="dateIcon">
                                    <v-icon>mdi-calendar</v-icon>
                                </template>
                                <template slot="timeIcon">
                                    <v-icon>mdi-clock</v-icon>
                                </template>
                            </v-datetime-picker>
                            <v-textarea
                                v-model="changeStatusDialogObservacaoExterna"
                                :label="$t('ticket.tasks.externalObservation')"
                                rows="2"
                                outlined
                                dense
                                :disabled="isTicketClosed"
                                class="mt-3"
                            />
                            <v-textarea
                                v-if="showInternalObservationEffective"
                                v-model="changeStatusDialogObservacaoInterna"
                                :label="$t('ticket.tasks.internalObservation')"
                                rows="2"
                                outlined
                                dense
                                hide-details
                                :disabled="isTicketClosed"
                                class="mt-3"
                            />
                            <div class="mt-3">
                                <div class="text-caption grey--text mb-1">{{ $t('ticket.tasks.attachFiles') || 'Anexos (fotos ou .zip)' }}</div>
                                <input
                                    ref="changeStatusAnexosInput"
                                    type="file"
                                    accept="image/*,.zip,application/zip"
                                    multiple
                                    class="d-none"
                                    @change="onChangeStatusAnexosSelected"
                                />
                                <v-btn small outlined @click="$refs.changeStatusAnexosInput && $refs.changeStatusAnexosInput.click()" :disabled="isTicketClosed">
                                    <v-icon left small>mdi-paperclip</v-icon>
                                    {{ $t('ticket.tasks.addAttachment') || 'Adicionar arquivo' }}
                                </v-btn>
                                <div v-if="changeStatusDialogAnexos.length" class="mt-2">
                                    <div v-for="(a, idx) in changeStatusDialogAnexos" :key="'st-' + idx" class="d-flex align-center mt-1">
                                        <v-icon small class="mr-1">mdi-file-{{ (a.mediaType || 'image') === 'zip' ? 'zip-box' : 'image' }}</v-icon>
                                        <span class="text-body-2 flex-grow-1 truncate">{{ a.file.name }}</span>
                                        <v-btn icon x-small @click="removeChangeStatusAnexo(idx)">
                                            <v-icon small>mdi-close</v-icon>
                                        </v-btn>
                                    </div>
                                </div>
                            </div>
                        </v-card-text>
                        <v-divider></v-divider>
                        <v-card-actions>
                            <v-spacer></v-spacer>
                            <v-btn text @click="changeStatusAtribuidoDialog = false">
                                {{ $t('button.cancel') }}
                            </v-btn>
                            <v-btn
                                color="primary"
                                :disabled="!changeStatusDialogFormValid || uploadingStatusAnexos"
                                :loading="uploadingStatusAnexos"
                                @click="confirmChangeStatusAtribuido"
                            >
                                {{ $t('button.confirm') }}
                            </v-btn>
                        </v-card-actions>
                    </v-card>
                </v-dialog>

                <!-- Status (dispatcher) -->
                <v-card class="pa-4 mb-4" outlined v-if="showStatusDispatcherChangeEffective">
                    <v-card-title class="text-h6 pa-0 mb-3">
                        <v-icon left color="primary">{{ isAgenciaCandidatosTicket ? 'mdi-view-list' : 'mdi-account-supervisor' }}</v-icon>
                        {{ isAgenciaCandidatosTicket ? $t('ticket.status.change') : $t('ticket.status.changeDispatcher') }}
                    </v-card-title>
                    <v-divider class="mb-3"></v-divider>
                    <v-row>
                        <v-col cols="12">
                            <v-select 
                                :value="editedItem.id_status_dispatcher" 
                                @input="$emit('update:statusDispatcher', $event)"
                                :items="status"
                                :item-text="rotuloStatus"
                                item-value="id_status_ticket"
                                :label="isAgenciaCandidatosTicket ? $t('ticket.status.select') : $t('ticket.status.selectDispatcher')"
                                :disabled="isTicketClosed || isTicketPendente"
                                dense
                                hide-details
                                clearable
                            />
                        </v-col>
                        <v-col v-if="isSuporteSistemaTicket && !isTicketPendente" cols="12">
                            <v-text-field
                                :value="editedItem.motivo_status"
                                @input="$emit('update:motivoStatus', $event)"
                                :label="$t('ticketSuporteFluxo.motivoStatus')"
                                :disabled="isTicketClosed"
                                dense
                                hide-details
                            ></v-text-field>
                        </v-col>
                    </v-row>
                </v-card>

                <!-- Team Card: só quando a Classificação do atendimento, que já traz o grupo, está oculta -->
                <v-card class="pa-4 mb-4" outlined v-if="showTeamEffective && showGenericTicketHierarchy && !showSelectionTreeEditEffective">
                    <v-card-title class="text-h6 pa-0 mb-3">
                        <v-icon left color="primary">mdi-account-group</v-icon>
                        {{ $t('ticket.team.title') }}
                    </v-card-title>
                    <v-divider class="mb-3"></v-divider>
                    <v-row>
                        <v-col cols="12">
                            <v-select
                                :value="teamSelectValue"
                                @input="$emit('update:team', $event)"
                                :items="equipes"
                                item-text="nome_equipe"
                                item-value="id_equipe"
                                :label="$t('ticket.team.select')"
                                dense
                                hide-details
                                clearable
                                :disabled="isTicketClosed"
                                :key="'eq-' + editedItem.id_ticket + '-' + (teamSelectValue != null ? teamSelectValue : '') + '-' + (editedItem.id_equipes || '')"
                            />
                        </v-col>
                    </v-row>
                </v-card>

                <!-- User Assignment Card: display current user + button to open assign dialog -->
                <v-card class="pa-4 mb-4" outlined v-if="showUserAssignmentEffective">
                    <v-card-title class="text-h6 pa-0 mb-3">
                        <v-icon left color="primary">mdi-account</v-icon>
                        {{ $t('ticket.usuario.label') }}
                    </v-card-title>
                    <v-divider class="mb-3"></v-divider>
                    <v-row>
                        <v-col cols="12">
                            <div class="text-body-1 font-weight-medium mb-2">
                                {{ assigneesDisplayLabel }}
                            </div>
                            <v-btn
                                color="primary"
                                outlined
                                small
                                :disabled="isTicketClosed"
                                @click="openAssignUserDialog"
                            >
                                <v-icon left small>mdi-account-plus</v-icon>
                                {{ hasTicketAssignees ? $t('ticket.tasks.changeAssignment') : $t('ticket.tasks.assignUser') }}
                            </v-btn>
                        </v-col>
                    </v-row>
                </v-card>

                <!-- Assign user / task dialog -->
                <v-dialog v-model="assignUserDialog" max-width="500" persistent>
                    <v-card>
                        <v-card-title class="text-h6">
                            <v-icon left color="primary">mdi-account-check</v-icon>
                            {{ $t('ticket.tasks.assignDialogTitle') }}
                        </v-card-title>
                        <v-divider></v-divider>
                        <v-card-text class="pt-4">
                            <v-textarea
                                v-model="assignDialogDescricao"
                                :label="$t('ticket.tasks.taskDescription')"
                                rows="3"
                                outlined
                                dense
                                hide-details
                                :disabled="isTicketClosed"
                                auto-grow
                            />
                            <v-autocomplete
                                v-model="assignDialogUserIds"
                                :items="usuariosPorEquipe"
                                item-text="nome_usuario"
                                item-value="id_usuario"
                                :label="$t('ticket.usuario.select')"
                                class="mt-3"
                                dense
                                outlined
                                hide-details
                                clearable
                                multiple
                                chips
                                small-chips
                                :menu-props="{ maxHeight: '300' }"
                                :disabled="isTicketClosed"
                            />
                            <v-datetime-picker
                                v-model="assignDialogDtPrevistaFim"
                                :label="$t('ticket.tasks.expectedCompletionDate')"
                                class="mt-3"
                                :time-picker-props="checklistTimeProps"
                                :text-field-props="checklistTextFieldProps"
                                time-format="HH:mm:ss"
                                date-format="dd/MM/yyyy"
                                :clear-text="$t('datetime.close')"
                                :disabled="isTicketClosed"
                            >
                                <template slot="dateIcon">
                                    <v-icon>mdi-calendar</v-icon>
                                </template>
                                <template slot="timeIcon">
                                    <v-icon>mdi-clock</v-icon>
                                </template>
                            </v-datetime-picker>
                            <div class="mt-3">
                                <div class="text-caption grey--text mb-1">{{ $t('ticket.tasks.attachFiles') || 'Anexos (fotos ou .zip)' }}</div>
                                <input
                                    ref="assignUserAnexosInput"
                                    type="file"
                                    accept="image/*,.zip,application/zip"
                                    multiple
                                    class="d-none"
                                    @change="onAssignUserAnexosSelected"
                                />
                                <v-btn small outlined @click="$refs.assignUserAnexosInput && $refs.assignUserAnexosInput.click()" :disabled="isTicketClosed">
                                    <v-icon left small>mdi-paperclip</v-icon>
                                    {{ $t('ticket.tasks.addAttachment') || 'Adicionar arquivo' }}
                                </v-btn>
                                <div v-if="assignDialogAnexos.length" class="mt-2">
                                    <div v-for="(a, idx) in assignDialogAnexos" :key="'au-' + idx" class="d-flex align-center mt-1">
                                        <v-icon small class="mr-1">mdi-file-{{ (a.mediaType || 'image') === 'zip' ? 'zip-box' : 'image' }}</v-icon>
                                        <span class="text-body-2 flex-grow-1 truncate">{{ a.file.name }}</span>
                                        <v-btn icon x-small @click="removeAssignUserAnexo(idx)">
                                            <v-icon small>mdi-close</v-icon>
                                        </v-btn>
                                    </div>
                                </div>
                            </div>
                        </v-card-text>
                        <v-divider></v-divider>
                        <v-card-actions>
                            <v-spacer></v-spacer>
                            <v-btn text @click="assignUserDialog = false">
                                {{ $t('button.cancel') }}
                            </v-btn>
                            <v-btn
                                color="primary"
                                :disabled="!assignDialogUserIds || assignDialogUserIds.length === 0 || uploadingAssignAnexos"
                                :loading="uploadingAssignAnexos"
                                @click="confirmAssignUser"
                            >
                                {{ $t('button.confirm') }}
                            </v-btn>
                        </v-card-actions>
                    </v-card>
                </v-dialog>

                <!-- Expected finalization date (dispatcher) -->
                <v-card class="pa-4 mb-4" outlined v-if="showExpectedFinalizationEditEffective && showGenericTicketHierarchy">
                    <v-card-title class="text-h6 pa-0 mb-3">
                        <v-icon left color="primary">mdi-calendar-check</v-icon>
                        {{ $t('ticket.tasks.expectedCompletionDate') }}
                    </v-card-title>
                    <v-divider class="mb-3"></v-divider>
                    <v-row>
                        <v-col cols="12">
                            <v-datetime-picker
                                :value="expectedFinalizationPickerDate"
                                @input="$emit('update:expectedFinalization', $event)"
                                :label="$t('ticket.tasks.expectedCompletionDate')"
                                :time-picker-props="checklistTimeProps"
                                :text-field-props="checklistTextFieldProps"
                                time-format="HH:mm:ss"
                                date-format="dd/MM/yyyy"
                                :clear-text="$t('datetime.close')"
                                :disabled="isTicketClosed"
                            >
                                <template slot="dateIcon">
                                    <v-icon>mdi-calendar</v-icon>
                                </template>
                                <template slot="timeIcon">
                                    <v-icon>mdi-clock</v-icon>
                                </template>
                            </v-datetime-picker>
                        </v-col>
                    </v-row>
                </v-card>

                <!-- SLA manual: só registro; o SLA exibido nas telas continua sendo o calculado -->
                <v-card class="pa-4 mb-4" outlined v-if="showSlaManualEditEffective">
                    <v-card-title class="text-h6 pa-0 mb-3">
                        <v-icon left color="primary">mdi-timer-edit-outline</v-icon>
                        {{ $t('ticket.sla.manual') }}
                    </v-card-title>
                    <v-divider class="mb-3"></v-divider>
                    <v-row>
                        <v-col cols="12">
                            <v-select
                                :value="editedItem.sla_manual || null"
                                @change="$emit('update:slaManual', $event || null)"
                                :items="slaManualItems"
                                :label="$t('ticket.sla.manual')"
                                :hint="$t('ticket.sla.manualHint')"
                                persistent-hint
                                clearable
                                outlined
                                dense
                            ></v-select>
                        </v-col>
                    </v-row>
                </v-card>

                <!-- Vehicle and Equipment Card (editable when ticket is not closed; show even with no pairs so user can add first) -->
                <v-card class="pa-4 mb-4" outlined v-if="showVehicleEquipmentEditEffective && !isTicketClosed && showGenericTicketHierarchy">
                    <v-card-title class="text-h6 pa-0 mb-3">
                        <v-icon left color="primary">mdi-car-cog</v-icon>
                        {{ $t('ticket.createDialog.tabs.equipamentos') }}
                    </v-card-title>
                    <v-divider class="mb-3"></v-divider>
                    <div v-if="empresa">
                        <v-row>
                            <v-col cols="12" v-if="showVehicleEditColumn">
                                <v-autocomplete
                                    :value="selectedVehicleId"
                                    @input="onVehicleInput($event)"
                                    :items="veiculosFiltradosPorEmpresa"
                                    item-text="displayText"
                                    item-value="id_veiculo"
                                    :label="$t('ticket.createDialog.equipamentos.selectVeiculo')"
                                    dense
                                    outlined
                                    hide-details
                                    clearable
                                    :loading="loadingVeiculos"
                                    :disabled="loadingVeiculos"
                                >
                                    <template v-slot:no-data>
                                        <div class="pa-2">
                                            {{ $t('ticket.createDialog.equipamentos.noVeiculos') }}
                                        </div>
                                    </template>
                                </v-autocomplete>
                            </v-col>
                            <v-col cols="12" v-if="showEquipmentEditColumn">
                                <v-autocomplete
                                    :value="selectedEquipamentos"
                                    @input="onEquipamentosInput($event)"
                                    :items="equipamentosForDetailSelect"
                                    item-text="displayText"
                                    item-value="id_equipamento"
                                    :label="$t('ticket.createDialog.equipamentos.selectEquipamento')"
                                    multiple
                                    chips
                                    small-chips
                                    dense
                                    outlined
                                    hide-details
                                    clearable
                                    :loading="loadingEquipamentos"
                                    :disabled="loadingEquipamentos"
                                >
                                    <template v-slot:append-outer>
                                        <div class="d-flex align-center" style="gap: 4px;">
                                            <v-tooltip v-if="showVehicleEditColumn" bottom>
                                                <template v-slot:activator="{ on, attrs }">
                                                    <v-btn
                                                        icon
                                                        small
                                                        v-bind="attrs"
                                                        v-on="on"
                                                        :disabled="!selectedVehicleId || loadingEquipamentos"
                                                        :color="filterEquipamentosByVehicle ? 'primary' : ''"
                                                        @click="toggleFilterEquipamentosByVehicle"
                                                    >
                                                        <v-icon small>{{ filterEquipamentosByVehicle ? 'mdi-filter' : 'mdi-filter-outline' }}</v-icon>
                                                    </v-btn>
                                                </template>
                                                <span>{{ filterEquipamentosByVehicle ? $t('ticket.createDialog.equipamentos.filterByVehicleActive') : $t('ticket.createDialog.equipamentos.filterByVehicle') }}</span>
                                            </v-tooltip>
                                            <v-tooltip bottom>
                                                <template v-slot:activator="{ on, attrs }">
                                                    <v-btn
                                                        icon
                                                        small
                                                        v-bind="attrs"
                                                        v-on="on"
                                                        color="primary"
                                                        :disabled="loadingEquipamentos"
                                                        @click="openEquipmentQRScanner"
                                                    >
                                                        <v-icon small>mdi-qrcode-scan</v-icon>
                                                    </v-btn>
                                                </template>
                                                <span>{{ $t('ticket.createDialog.equipamentos.scanQR') || 'Ler QR Code do equipamento' }}</span>
                                            </v-tooltip>
                                        </div>
                                    </template>
                                    <template v-slot:selection="{ item, index: chipIndex }">
                                        <v-chip
                                            v-if="chipIndex < (selectedEquipamentos || []).length"
                                            small
                                            class="mr-2"
                                        >
                                            {{ item && item.displayText ? item.displayText : (getEquipamentoDisplayTextById ? getEquipamentoDisplayTextById(item && item.id_equipamento != null ? item.id_equipamento : item) : item) }}
                                        </v-chip>
                                    </template>
                                    <template v-slot:no-data>
                                        <div class="pa-2">
                                            {{ $t('ticket.createDialog.equipamentos.noEquipamentos') }}
                                        </div>
                                    </template>
                                </v-autocomplete>
                            </v-col>
                        </v-row>
                    </div>
                    <v-alert v-else type="info" dense class="mb-0">
                        {{ $t('ticket.createDialog.equipamentos.noEmpresaSelected') }}
                    </v-alert>
                </v-card>
                    </v-col>
                </v-row>
                </div>
            </v-tab-item>

            <!-- Tab: Checklist / Arquivos -->
            <v-tab-item v-if="showChecklistSectionEffective" value="tab-arquivos">
                <div class="detail-tab-content">
                <v-card class="pa-4 mb-4" outlined>
                    <v-card-title class="text-h6 pa-0 mb-3">
                        <v-icon left color="primary">{{ detailSecondaryTabIcon }}</v-icon>
                        {{ checklistTabDisplayName }}
                        <v-spacer></v-spacer>
                        <v-btn 
                            v-if="editedItem.id_checklist_equipamento && editChecklist && !readOnlyTicketDetail && !isSuporteSistemaTicket && !isAgenciaCandidatosTicket" 
                            small 
                            color="primary"
                            @click="$emit('open-checklist-dialog', editedItem)"
                            :disabled="isTicketClosed"
                        >
                            {{ $t('ticket.checklist.fulfillChecklist') }}
                        </v-btn>
                    </v-card-title>
                    <v-divider class="mb-3"></v-divider>

                    <div v-if="isSuporteSistemaTicket">
                        <div v-if="loadingSuporteAnexos" class="d-flex justify-center pa-4">
                            <v-progress-circular indeterminate color="primary"></v-progress-circular>
                        </div>
                        <v-row v-else-if="suporteSistemaAnexos.length > 0" dense>
                            <v-col
                                v-for="(anexo, idx) in suporteSistemaAnexos"
                                :key="'ss-tab-anexo-' + (anexo.id_anexo || idx)"
                                cols="6" sm="4" md="3"
                            >
                                <template v-if="getSuporteSistemaAnexoType(anexo) === 'zip'">
                                    <div
                                        class="rounded grey lighten-2 d-flex align-center justify-center pa-2"
                                        style="min-height: 100px; cursor: pointer;"
                                        @click="openPhoto(getPhotoUrl(anexo.caminho_arquivo))"
                                    >
                                        <v-icon large>mdi-folder-zip</v-icon>
                                    </div>
                                </template>
                                <template v-else-if="getSuporteSistemaAnexoType(anexo) === 'document'">
                                    <div
                                        class="rounded grey lighten-2 d-flex align-center justify-center pa-2"
                                        style="min-height: 100px; cursor: pointer;"
                                        @click="openPhoto(getPhotoUrl(anexo.caminho_arquivo))"
                                    >
                                        <v-icon large>mdi-file-document-outline</v-icon>
                                    </div>
                                </template>
                                <template v-else>
                                    <v-img
                                        :src="getPhotoUrl(anexo.caminho_arquivo)"
                                        max-height="200"
                                        max-width="100%"
                                        contain
                                        class="rounded"
                                        style="cursor: pointer"
                                        @click="openPhoto(getPhotoUrl(anexo.caminho_arquivo))"
                                        @error="handleImageError"
                                    >
                                        <template v-slot:placeholder>
                                            <v-row class="fill-height ma-0" align="center" justify="center">
                                                <v-progress-circular indeterminate color="grey lighten-5"></v-progress-circular>
                                            </v-row>
                                        </template>
                                    </v-img>
                                </template>
                                <span class="caption d-block text-center text-truncate mt-1">
                                    {{ anexo.nome_arquivo_original || $t('equipmentChecklist.image') || 'Anexo' }}
                                </span>
                            </v-col>
                        </v-row>
                        <v-alert v-else type="info" text dense class="mb-0">
                            {{ $t('ticketSuporteSistema.noAnexos') || $t('ticketSuporteSistema.noPhotos') || 'Nenhum anexo associado a este ticket.' }}
                        </v-alert>
                    </div>

                    <div v-else-if="loadingChecklistDetails" class="d-flex justify-center pa-4">
                        <v-progress-circular indeterminate color="primary"></v-progress-circular>
                    </div>

                    <div v-else-if="checklistSelecionado">
                        <div class="subtitle-2 mb-2 primary--text">
                            {{ isAgenciaCandidatosTicket ? (agenciaCandidatosUploadedFileName || checklistSelecionado.nome_checklist) : checklistSelecionado.nome_checklist }}
                        </div>

                        <!-- Equipment info - only show if checklist has been fulfilled -->
                        <v-row dense class="mb-2" v-if="checklistItem.nome_equipamento || checklistItem.dt_alteracao_extenso || checklistItem.dt_realizacao_extenso || checklistItem.dt_realizacao">
                            <v-col cols="12" sm="6" v-if="checklistItem.nome_equipamento">
                                <div class="caption grey--text">
                                    {{ $t('equipmentChecklist.equipment') }}
                                </div>
                                <div class="body-2">
                                    {{ checklistItem.nome_equipamento }}
                                </div>
                            </v-col>
                            <v-col cols="12" sm="6" v-if="checklistItem.dt_alteracao_extenso || checklistItem.dt_realizacao_extenso || checklistItem.dt_realizacao">
                                <div class="caption grey--text">
                                    {{ $t('equipmentChecklist.realizationDateTime') }}
                                </div>
                                <div class="body-2">
                                    {{ checklistItem.dt_alteracao_extenso || checklistItem.dt_realizacao_extenso || (checklistItem.dt_realizacao ? new Date(checklistItem.dt_realizacao).toLocaleString() : '-') }}
                                </div>
                            </v-col>
                        </v-row>

                        <v-divider class="my-2" v-if="(checklistItem.nome_equipamento || checklistItem.dt_alteracao_extenso || checklistItem.dt_realizacao_extenso || checklistItem.dt_realizacao) && checklistSelecionado.campos && checklistSelecionado.campos.length > 0"></v-divider>

                        <!-- Fields -->
                        <div v-if="checklistSelecionado.campos && checklistSelecionado.campos.length > 0">
                            <div 
                                v-for="campo in checklistSelecionado.campos"
                                :key="`detail-campo-${campo.id_campo}`"
                                class="mb-3"
                            >
                                <div class="caption grey--text">{{ campo.label }}</div>

                                <!-- Text/Number/Select/Date/Textarea -->
                                <div v-if="['Text', 'Number', 'Select', 'Date', 'Textarea'].includes(campo.tipo)" class="body-1">
                                    {{ checklistValoresCampos[campo.id_campo] || '-' }}
                                </div>

                                <!-- Checkbox -->
                                <div v-else-if="campo.tipo === 'Checkbox'" class="body-1">
                                    <v-icon :color="checklistValoresCampos[campo.id_campo] ? 'success' : 'grey'">
                                        {{ checklistValoresCampos[campo.id_campo] ? 'mdi-checkbox-marked' : 'mdi-checkbox-blank-outline' }}
                                    </v-icon>
                                    {{ checklistValoresCampos[campo.id_campo] ? $t('button.confirm') : $t('button.dismiss') }}
                                </div>

                                <!-- Photo -->
                                <div v-else-if="campo.tipo === 'Photo'">
                                    <div v-if="checklistValoresCampos[campo.id_campo]">
                                        <v-img
                                            :src="getPhotoUrl(checklistValoresCampos[campo.id_campo])"
                                            max-height="400"
                                            max-width="600"
                                            contain
                                            class="mt-2"
                                            @error="handleImageError"
                                            @click="openPhoto(getPhotoUrl(checklistValoresCampos[campo.id_campo]))"
                                            style="cursor: pointer"
                                        >
                                            <template v-slot:placeholder>
                                                <v-row class="fill-height ma-0" align="center" justify="center">
                                                    <v-progress-circular indeterminate color="grey lighten-5"></v-progress-circular>
                                                </v-row>
                                            </template>
                                        </v-img>
                                    </div>
                                    <div v-else class="grey--text font-italic small">
                                        {{ $t('equipmentChecklist.noPhoto') }}
                                    </div>
                                </div>

                                <!-- Who/when edited this field -->
                                <div v-if="checklistValoresEditedBy[campo.id_campo] && (checklistValoresEditedBy[campo.id_campo].nome_usuario_alteracao || checklistValoresEditedBy[campo.id_campo].dt_alteracao_extenso)" class="caption grey--text mt-1">
                                    {{ $t('ticket.print.editedBy') || 'Editado por' }}: {{ checklistValoresEditedBy[campo.id_campo].nome_usuario_alteracao || '-' }}<template v-if="checklistValoresEditedBy[campo.id_campo].dt_alteracao_extenso"> — {{ checklistValoresEditedBy[campo.id_campo].dt_alteracao_extenso }}</template>
                                </div>
                            </div>
                        </div>
                        <div v-else-if="checklistSelecionado.campos && checklistSelecionado.campos.length === 0" class="mb-3">
                            <v-alert type="info" text dense>
                                {{ $t('equipmentChecklist.noFieldsAvailable') }}
                            </v-alert>
                        </div>
                        <div v-else-if="!checklistItem.nome_equipamento && !checklistItem.dt_alteracao_extenso && !checklistItem.dt_realizacao_extenso && !checklistItem.dt_realizacao" class="mb-3">
                            <v-alert type="info" text dense>
                                {{ $t('ticket.checklist.notFulfilled') || 'Checklist não foi preenchido ainda' }}
                            </v-alert>
                        </div>

                        <!-- Observations -->
                        <div v-if="checklistItem.observacoes">
                            <v-divider class="my-2"></v-divider>
                            <div class="caption grey--text">
                                {{ $t('equipmentChecklist.observations') }}
                            </div>
                            <div class="body-2">{{ checklistItem.observacoes }}</div>
                        </div>

                        <!-- Additional photos, videos and ZIP -->
                        <div v-if="checklistItem.fotos_extra && checklistItem.fotos_extra.length" class="mt-3">
                            <v-divider class="my-2"></v-divider>
                            <div class="caption grey--text mb-2">
                                {{ $t('equipmentChecklist.additionalPhotos') }}
                            </div>
                            <v-row dense>
                                <v-col v-for="(path, idx) in checklistItem.fotos_extra" :key="'extra-' + idx" cols="6" sm="4" md="3">
                                    <template v-if="getChecklistMediaTypeFromPath(path) === 'video'">
                                        <div class="rounded overflow-hidden grey lighten-2" style="max-height: 120px; max-width: 160px;">
                                            <video
                                                :src="getPhotoUrl(path)"
                                                muted
                                                playsinline
                                                preload="metadata"
                                                style="max-height: 120px; max-width: 160px; width: 100%; height: auto; object-fit: contain; display: block; cursor: pointer;"
                                                class="rounded"
                                                @click="openPhoto(getPhotoUrl(path))"
                                            ></video>
                                        </div>
                                        <span class="caption d-block text-center">{{ $t('equipmentChecklist.video') }}</span>
                                    </template>
                                    <template v-else-if="getChecklistMediaTypeFromPath(path) === 'zip'">
                                        <div class="rounded grey lighten-2 d-flex align-center justify-center pa-2" style="min-height: 100px; cursor: pointer;" @click="openPhoto(getPhotoUrl(path))">
                                            <v-icon large>mdi-folder-zip</v-icon>
                                        </div>
                                        <span class="caption d-block text-center">{{ $t('equipmentChecklist.zipFile') }}</span>
                                    </template>
                                    <template v-else>
                                        <v-img
                                            :src="getPhotoUrl(path)"
                                            max-height="120"
                                            max-width="160"
                                            contain
                                            class="rounded"
                                            style="cursor: pointer"
                                            @click="openPhoto(getPhotoUrl(path))"
                                            @error="handleImageError"
                                        >
                                            <template v-slot:placeholder>
                                                <v-row class="fill-height ma-0" align="center" justify="center">
                                                    <v-progress-circular indeterminate color="grey lighten-5"></v-progress-circular>
                                                </v-row>
                                            </template>
                                        </v-img>
                                    </template>
                                </v-col>
                            </v-row>
                        </div>

                        <template v-if="isAgenciaCandidatosTicket">
                            <v-divider class="my-4"></v-divider>
                            <div class="subtitle-2 mb-2 primary--text">
                                {{ $t('ticket.detail.precandidatos.title') }}
                                <v-chip v-if="precExtrasFinalizadas" x-small color="success" outlined class="ml-2">{{ $t('ticket.detail.precandidatos.extrasFinalized') }}</v-chip>
                            </div>
                            <div v-if="!loadingPrecandidatos" class="d-flex flex-wrap align-center mb-1">
                                <v-btn
                                    small
                                    outlined
                                    color="primary"
                                    :disabled="!precandidatosList.length || precDownloadExpirado"
                                    :loading="precCsvDownloading"
                                    @click="downloadPrecandidatosCsv"
                                >
                                    <v-icon left small>mdi-download</v-icon>
                                    {{ $t('ticket.detail.precandidatos.downloadCsv') }}
                                </v-btn>
                                <v-btn
                                    v-if="!precCandidatosWriteDisabled && !hidePrecandidatosWriteActions"
                                    small
                                    outlined
                                    color="primary"
                                    class="ml-2"
                                    :loading="precCsvUploading"
                                    @click="triggerPrecandidatosCsvUpload"
                                >
                                    <v-icon left small>mdi-upload</v-icon>
                                    {{ $t('ticket.detail.precandidatos.uploadCsv') }}
                                </v-btn>
                                <input
                                    ref="precandidatosCsvInput"
                                    type="file"
                                    accept=".csv,.xlsx,.xls"
                                    class="d-none"
                                    @change="onPrecandidatosCsvSelected"
                                />
                            </div>
                            <p v-if="!loadingPrecandidatos" class="caption grey--text mb-2">{{ $t('ticket.detail.precandidatos.csvHint') }}</p>
                            <p v-if="!loadingPrecandidatos && precDownloadPrazoHint" class="caption mb-2" :class="precDownloadExpirado ? 'error--text' : 'grey--text'">{{ precDownloadPrazoHint }}</p>
                            <div v-if="loadingPrecandidatos" class="d-flex justify-center pa-4">
                                <v-progress-circular indeterminate color="primary" size="32"></v-progress-circular>
                            </div>
                            <v-data-table
                                v-else-if="precandidatosList.length > 0"
                                dense
                                item-key="id_pre_candidato"
                                :headers="precandidatosHeaders"
                                :items="precandidatosList"
                                :items-per-page="10"
                                class="elevation-1"
                                :footer-props="{ itemsPerPageOptions: [10, 25, 50] }"
                            >
                                <div slot="item.zonas_risco_label" slot-scope="{ item }">
                                    <ZonaRiscoChip :rotulo="item.zonas_risco_label" />
                                </div>
                                <div slot="item.validacao" slot-scope="{ item }">
                                    <v-select
                                        dense
                                        hide-details
                                        class="mt-0 pt-0 prec-validacao-select"
                                        :items="precSituacoesList"
                                        item-text="nome"
                                        item-value="id_situacao_validacao"
                                        :value="precValidacaoValue(item)"
                                        :disabled="precCandidatosWriteDisabled || precSavingId === item.id_pre_candidato || !precSituacoesList.length"
                                        @input="onPrecValidacaoSelectChange(item, $event)"
                                    />
                                </div>
                                <div
                                    v-for="t in precTurnosSorted"
                                    :key="'prec-tv-slot-a-' + t.id_turno"
                                    :slot="turnoPrecValSlot(t)"
                                    slot-scope="{ item }"
                                >
                                    <v-select
                                        v-if="!precTiposTurnosPrecSorted.length"
                                        dense
                                        hide-details
                                        class="mt-0 pt-0 prec-turno-per-shift-select"
                                        :title="precTurnoColumnTooltip(t)"
                                        :items="precEnderecoPorTurnoSelectItems"
                                        item-value="value"
                                        item-text="text"
                                        :value="precValidacaoPorTurnoValue(item, t)"
                                        :disabled="precCandidatosWriteDisabled || precSavingId === item.id_pre_candidato"
                                        @input="onPrecValidacaoPorTurnoChange(item, t, $event)"
                                    />
                                </div>
                                <div
                                    v-for="tt in precTiposTurnosPrecSorted"
                                    :key="'prec-tt-slot-a-' + tt.id_tipo_turno_precandidato"
                                    :slot="tipoPrecValSlot(tt)"
                                    slot-scope="{ item }"
                                >
                                    <v-text-field
                                        dense
                                        hide-details
                                        class="mt-0 pt-0 prec-tipo-turno-text"
                                        :value="precTipoTurnoTextValue(item, tt.id_tipo_turno_precandidato)"
                                        :disabled="precCandidatosWriteDisabled || precSavingId === item.id_pre_candidato"
                                        @blur="onPrecTipoTurnoTextBlur(item, tt, $event)"
                                    />
                                </div>
                            </v-data-table>
                            <v-alert v-else type="info" text dense class="mb-0">
                                {{ $t('ticket.detail.precandidatos.empty') }}
                            </v-alert>
                            <div v-if="precExtrasItems.length" class="mt-4">
                                    <div class="d-flex align-center mb-2">
                                        <div class="subtitle-2 primary--text">{{ $t('ticket.detail.precandidatos.extrasTitle') }}</div>
                                        <v-btn small outlined color="primary" class="ml-3" :disabled="precDownloadExpirado" @click="downloadPrecExtrasCsv">
                                            <v-icon left small>mdi-download</v-icon>
                                            {{ $t('ticket.detail.precandidatos.downloadExtrasCsv') }}
                                        </v-btn>
                                    </div>
                                <v-data-table
                                    dense
                                    :headers="precExtrasHeaders"
                                    :items="precExtrasItems"
                                    item-key="id_pre_candidato"
                                    :items-per-page="10"
                                >
                                    <div slot="item.zonas_risco_label" slot-scope="{ item }">
                                        <ZonaRiscoChip :rotulo="item.zonas_risco_label" />
                                    </div>
                                    <div slot="item.validacao" slot-scope="{ item }">
                                        <v-select
                                            dense
                                            hide-details
                                            class="mt-0 pt-0 prec-validacao-select"
                                            :items="precSituacoesList"
                                            item-text="nome"
                                            item-value="id_situacao_validacao"
                                            :value="precValidacaoValue(item._base)"
                                            :disabled="precCandidatosWriteDisabled || precSavingId === item.id_pre_candidato || !precSituacoesList.length"
                                            @input="onPrecValidacaoSelectChange(item._base, $event)"
                                        />
                                    </div>
                                <div
                                    v-for="c in precColunasExtras"
                                    :key="'prec-extra-slot-' + c.id_coluna"
                                    :slot="'item.extra_' + c.id_coluna"
                                    slot-scope="{ item }"
                                >
                                    <v-text-field
                                        dense
                                        hide-details
                                        class="mt-0 pt-0 prec-tipo-turno-text"
                                        :value="item['extra_' + c.id_coluna] == null ? '' : String(item['extra_' + c.id_coluna])"
                                        :disabled="precCandidatosWriteDisabled || precSavingId === item.id_pre_candidato"
                                        @blur="onPrecExtraTextBlur(item, c, $event)"
                                    />
                                </div>
                                </v-data-table>
                            </div>
                        </template>

                    </div>
                    <div v-else-if="isAgenciaCandidatosTicket">
                        <div class="subtitle-2 mb-2 primary--text">
                            {{ $t('ticket.detail.precandidatos.title') }}
                            <v-chip v-if="precExtrasFinalizadas" x-small color="success" outlined class="ml-2">{{ $t('ticket.detail.precandidatos.extrasFinalized') }}</v-chip>
                        </div>
                        <div v-if="!loadingPrecandidatos" class="d-flex flex-wrap align-center mb-1">
                            <v-btn
                                small
                                outlined
                                color="primary"
                                :disabled="!precandidatosList.length || precDownloadExpirado"
                                :loading="precCsvDownloading"
                                @click="downloadPrecandidatosCsv"
                            >
                                <v-icon left small>mdi-download</v-icon>
                                {{ $t('ticket.detail.precandidatos.downloadCsv') }}
                            </v-btn>
                            <v-btn
                                v-if="!precCandidatosWriteDisabled && !hidePrecandidatosWriteActions"
                                small
                                outlined
                                color="primary"
                                class="ml-2"
                                :loading="precCsvUploading"
                                @click="triggerPrecandidatosCsvUpload"
                            >
                                <v-icon left small>mdi-upload</v-icon>
                                {{ $t('ticket.detail.precandidatos.uploadCsv') }}
                            </v-btn>
                            <input
                                ref="precandidatosCsvInputAgencia"
                                type="file"
                                accept=".csv,.xlsx,.xls"
                                class="d-none"
                                @change="onPrecandidatosCsvSelected"
                            />
                        </div>
                        <p v-if="!loadingPrecandidatos" class="caption grey--text mb-2">{{ $t('ticket.detail.precandidatos.csvHint') }}</p>
                        <p v-if="!loadingPrecandidatos && precDownloadPrazoHint" class="caption mb-2" :class="precDownloadExpirado ? 'error--text' : 'grey--text'">{{ precDownloadPrazoHint }}</p>
                        <div v-if="loadingPrecandidatos" class="d-flex justify-center pa-4">
                            <v-progress-circular indeterminate color="primary" size="32"></v-progress-circular>
                        </div>
                        <v-data-table
                            v-else-if="precandidatosList.length > 0"
                            dense
                            item-key="id_pre_candidato"
                            :headers="precandidatosHeaders"
                            :items="precandidatosList"
                            :items-per-page="10"
                            class="elevation-1"
                            :footer-props="{ itemsPerPageOptions: [10, 25, 50] }"
                        >
                            <div slot="item.zonas_risco_label" slot-scope="{ item }">
                                <ZonaRiscoChip :rotulo="item.zonas_risco_label" />
                            </div>
                            <div slot="item.validacao" slot-scope="{ item }">
                                <v-select
                                    dense
                                    hide-details
                                    class="mt-0 pt-0 prec-validacao-select"
                                    :items="precSituacoesList"
                                    item-text="nome"
                                    item-value="id_situacao_validacao"
                                    :value="precValidacaoValue(item)"
                                    :disabled="precCandidatosWriteDisabled || precSavingId === item.id_pre_candidato || !precSituacoesList.length"
                                    @input="onPrecValidacaoSelectChange(item, $event)"
                                />
                            </div>
                            <div
                                v-for="t in precTurnosSorted"
                                :key="'prec-tv-slot-b-' + t.id_turno"
                                :slot="turnoPrecValSlot(t)"
                                slot-scope="{ item }"
                            >
                                <v-select
                                    v-if="!precTiposTurnosPrecSorted.length"
                                    dense
                                    hide-details
                                    class="mt-0 pt-0 prec-turno-per-shift-select"
                                    :title="precTurnoColumnTooltip(t)"
                                    :items="precEnderecoPorTurnoSelectItems"
                                    item-value="value"
                                    item-text="text"
                                    :value="precValidacaoPorTurnoValue(item, t)"
                                    :disabled="precCandidatosWriteDisabled || precSavingId === item.id_pre_candidato"
                                    @input="onPrecValidacaoPorTurnoChange(item, t, $event)"
                                />
                            </div>
                            <div
                                v-for="tt in precTiposTurnosPrecSorted"
                                :key="'prec-tt-slot-b-' + tt.id_tipo_turno_precandidato"
                                :slot="tipoPrecValSlot(tt)"
                                slot-scope="{ item }"
                            >
                                <v-text-field
                                    dense
                                    hide-details
                                    class="mt-0 pt-0 prec-tipo-turno-text"
                                    :value="precTipoTurnoTextValue(item, tt.id_tipo_turno_precandidato)"
                                    :disabled="precCandidatosWriteDisabled || precSavingId === item.id_pre_candidato"
                                    @blur="onPrecTipoTurnoTextBlur(item, tt, $event)"
                                />
                            </div>
                        </v-data-table>
                        <v-alert v-else type="info" text dense class="mb-0">
                            {{ $t('ticket.detail.precandidatos.empty') }}
                        </v-alert>
                        <div v-if="precExtrasItems.length" class="mt-4">
                                <div class="d-flex align-center mb-2">
                                    <div class="subtitle-2 primary--text">{{ $t('ticket.detail.precandidatos.extrasTitle') }}</div>
                                    <v-btn small outlined color="primary" class="ml-3" :disabled="precDownloadExpirado" @click="downloadPrecExtrasCsv">
                                        <v-icon left small>mdi-download</v-icon>
                                        {{ $t('ticket.detail.precandidatos.downloadExtrasCsv') }}
                                    </v-btn>
                                </div>
                            <v-data-table
                                dense
                                :headers="precExtrasHeaders"
                                :items="precExtrasItems"
                                item-key="id_pre_candidato"
                                :items-per-page="10"
                            >
                                <div slot="item.zonas_risco_label" slot-scope="{ item }">
                                    <ZonaRiscoChip :rotulo="item.zonas_risco_label" />
                                </div>
                                <div slot="item.validacao" slot-scope="{ item }">
                                    <v-select
                                        dense
                                        hide-details
                                        class="mt-0 pt-0 prec-validacao-select"
                                        :items="precSituacoesList"
                                        item-text="nome"
                                        item-value="id_situacao_validacao"
                                        :value="precValidacaoValue(item._base)"
                                        :disabled="precCandidatosWriteDisabled || precSavingId === item.id_pre_candidato || !precSituacoesList.length"
                                        @input="onPrecValidacaoSelectChange(item._base, $event)"
                                    />
                                </div>
                            <div
                                v-for="c in precColunasExtras"
                                :key="'prec-extra-slot-' + c.id_coluna"
                                :slot="'item.extra_' + c.id_coluna"
                                slot-scope="{ item }"
                            >
                                <v-text-field
                                    dense
                                    hide-details
                                    class="mt-0 pt-0 prec-tipo-turno-text"
                                    :value="item['extra_' + c.id_coluna] == null ? '' : String(item['extra_' + c.id_coluna])"
                                    :disabled="precCandidatosWriteDisabled || precSavingId === item.id_pre_candidato"
                                    @blur="onPrecExtraTextBlur(item, c, $event)"
                                />
                            </div>
                            </v-data-table>
                        </div>
                    </div>
                    <div v-else-if="editedItem.id_checklist_equipamento">
                        <v-alert type="info" text dense class="mb-0">
                            {{ $t('ticket.checklist.notAvailable') }}
                        </v-alert>
                    </div>
                </v-card>
                </div>
            </v-tab-item>

            <!-- Tab: Tasks and Comments -->
            <v-tab-item value="tab-tasks">
                <div class="detail-tab-content">
                <v-card
                    v-if="showTasksTabStatusChangeCta"
                    class="pa-4 mb-4"
                    outlined
                >
                    <v-card-title class="text-subtitle-1 pa-0 mb-2">
                        <v-icon left color="primary" small>mdi-account-wrench</v-icon>
                        {{ $t('ticket.status.changeAtribuido') }}
                    </v-card-title>
                    <v-btn
                        color="primary"
                        outlined
                        block
                        class="mt-1"
                        :disabled="isTicketPendente"
                        @click="openChangeStatusAtribuidoDialog"
                    >
                        <v-icon left small>mdi-update</v-icon>
                        {{ $t('ticket.tasks.changeStatus') }}
                    </v-btn>
                </v-card>
                <!-- Tasks Card: all tasks linked to the ticket (status = technician / task workflow) -->
                <v-card class="pa-4 mb-4" outlined ref="tasksCard" v-if="editedItem.id_ticket">
                    <v-card-title class="text-h6 pa-0 mb-3">
                        <v-icon left color="primary">mdi-format-list-numbered</v-icon>
                        {{ $t('ticket.tasks.title') || 'Tarefas' }}
                    </v-card-title>
                    <v-divider class="mb-3"></v-divider>
                    <div v-if="loadingTasks" class="d-flex justify-center pa-4">
                        <v-progress-circular indeterminate color="primary"></v-progress-circular>
                    </div>
                    <div v-else-if="!ticketTasks || ticketTasks.length === 0" class="text-body-2 grey--text">
                        {{ $t('ticket.tasks.empty') || 'Nenhuma tarefa registrada.' }}
                    </div>
                    <div v-else>
                        <div
                            v-for="(task, tidx) in ticketTasks"
                            :key="'task-' + task.suffix + '-' + task.id_ticket"
                            class="text-body-2"
                            :class="{ 'mb-2': tidx < ticketTasks.length - 1 }"
                        >
                            <v-divider v-if="tidx > 0" class="mb-4"></v-divider>
                            <div class="d-flex align-center mb-2">
                                <span class="text-caption grey--text mr-2">{{ $t('ticket.tasks.taskId') || 'ID Tarefa' }}:</span>
                                <span class="font-weight-medium">{{ task.taskId }}</span>
                            </div>
                            <div class="d-flex align-start mb-2">
                                <span class="text-caption grey--text mr-2">{{ $t('ticket.tasks.taskDescription') || 'Tarefa' }}:</span>
                                <span>{{ task.descricao_tarefa || '-' }}</span>
                            </div>
                            <div class="d-flex align-center mb-2">
                                <span class="text-caption grey--text mr-2">{{ $t('ticket.usuario.assigned') || 'Atribuído' }}:</span>
                                <span>{{ taskTabTechnicianLine(task) }}</span>
                            </div>
                            <div class="d-flex align-center flex-wrap mb-2">
                                <span class="text-caption grey--text mr-2">{{ $t('ticket.status.self') || 'Status' }}:</span>
                                <v-select
                                    v-if="taskRowCanEditStatus(task)"
                                    :value="task.id_status_atribuido"
                                    @input="onInlineTaskStatusChange(task, $event)"
                                    :items="taskStatusSelectItems"
                                    :item-text="rotuloStatus"
                                    item-value="id_status_ticket"
                                    dense
                                    hide-details
                                    class="task-status-select"
                                    style="max-width: 280px;"
                                    :loading="savingTaskSuffix === task.suffix"
                                    :disabled="savingTaskSuffix === task.suffix || isTicketClosed"
                                />
                                <span v-else>{{ task.task_status_extenso || '-' }}</span>
                            </div>
                            <div class="d-flex align-center mb-2">
                                <span class="text-caption grey--text mr-2">{{ $t('ticket.tasks.date') || 'Data' }}:</span>
                                <span>{{ task.dt_alteracao_extenso || '-' }}</span>
                            </div>
                            <div class="d-flex align-center mb-2">
                                <span class="text-caption grey--text mr-2">{{ $t('ticket.tasks.expectedCompletionDate') || 'Previsão conclusão' }}:</span>
                                <span>{{ task.dt_prevista_fim_extenso || '-' }}</span>
                            </div>
                            <div class="d-flex align-center mb-2">
                                <span class="text-caption grey--text mr-2">{{ $t('ticket.tasks.dateTimeInitial') || 'Dt/hora inicial' }}:</span>
                                <span>{{ task.dt_inicio_extenso || '-' }}</span>
                            </div>
                            <div class="d-flex align-center mb-2">
                                <span class="text-caption grey--text mr-2">{{ $t('ticket.tasks.dateTimeFinal') || 'Dt/hora final' }}:</span>
                                <span>{{ task.dt_fim_extenso || '-' }}</span>
                            </div>
                            <div class="d-flex align-start mb-2">
                                <span class="text-caption grey--text mr-2">{{ $t('ticket.tasks.externalObservation') || 'Obs. externa' }}:</span>
                                <span>{{ task.observacao_externa || '-' }}</span>
                            </div>
                            <div class="d-flex align-start" v-if="showInternalObservationEffective">
                                <span class="text-caption grey--text mr-2">{{ $t('ticket.tasks.internalObservation') || 'Obs. interna' }}:</span>
                                <span>{{ task.observacao_interna || '-' }}</span>
                            </div>
                        </div>
                    </div>
                </v-card>

                <!-- Task attachments: only photos/files of the last task -->
                <v-card class="pa-4 mb-4" outlined v-if="editedItem.id_ticket && (ticketAnexosLastTask.length > 0 || loadingAnexos)">
                    <v-card-title class="text-h6 pa-0 mb-3">
                        <v-icon left color="primary">mdi-paperclip</v-icon>
                        {{ $t('ticket.tasks.attachments') }}
                    </v-card-title>
                    <v-divider class="mb-3"></v-divider>
                    <div v-if="loadingAnexos" class="d-flex justify-center pa-2">
                        <v-progress-circular indeterminate color="primary" size="24"></v-progress-circular>
                    </div>
                    <v-row v-else dense>
                        <v-col v-for="(anexo, idx) in ticketAnexosLastTask" :key="'anexo-' + anexo.id_ticket_anexo + '-' + idx" cols="6" sm="4" md="3">
                            <div class="text-caption grey--text mb-1">
                                {{ anexo.dt_inclusao_extenso || '-' }}
                                <span v-if="anexo.tipo_alteracao">({{ anexo.tipo_alteracao === 'status' ? ($t('ticket.status.self') || 'Status') : ($t('ticket.usuario.assigned') || 'Atribuição') }})</span>
                            </div>
                            <template v-if="getChecklistMediaTypeFromPath(anexo.caminho_arquivo) === 'video'">
                                <div class="rounded overflow-hidden grey lighten-2" style="max-height: 120px; max-width: 160px;">
                                    <video
                                        :src="getPhotoUrl(anexo.caminho_arquivo)"
                                        muted
                                        playsinline
                                        preload="metadata"
                                        style="max-height: 120px; max-width: 160px; width: 100%; height: auto; object-fit: contain; display: block; cursor: pointer;"
                                        class="rounded"
                                        @click="openPhoto(getPhotoUrl(anexo.caminho_arquivo))"
                                    ></video>
                                </div>
                                <span class="caption d-block text-center">{{ $t('equipmentChecklist.video') }}</span>
                            </template>
                            <template v-else-if="getChecklistMediaTypeFromPath(anexo.caminho_arquivo) === 'zip'">
                                <div class="rounded grey lighten-2 d-flex align-center justify-center pa-2" style="min-height: 100px; cursor: pointer;" @click="openPhoto(getPhotoUrl(anexo.caminho_arquivo))">
                                    <v-icon large>mdi-folder-zip</v-icon>
                                </div>
                                <span class="caption d-block text-center">{{ $t('equipmentChecklist.zipFile') }}</span>
                            </template>
                            <template v-else>
                                <v-img
                                    :src="getPhotoUrl(anexo.caminho_arquivo)"
                                    max-height="120"
                                    max-width="160"
                                    contain
                                    class="rounded"
                                    style="cursor: pointer"
                                    @click="openPhoto(getPhotoUrl(anexo.caminho_arquivo))"
                                    @error="handleImageError"
                                >
                                    <template v-slot:placeholder>
                                        <v-row class="fill-height ma-0" align="center" justify="center">
                                            <v-progress-circular indeterminate color="grey lighten-5"></v-progress-circular>
                                        </v-row>
                                    </template>
                                </v-img>
                                <span class="caption d-block text-center">{{ $t('equipmentChecklist.image') || 'Foto' }}</span>
                            </template>
                        </v-col>
                    </v-row>
                </v-card>

                <!-- Closing observations (supervisor/dispatcher) -->
                <v-card class="pa-4 mb-4" outlined v-if="showClosingObservationsEffective && editedItem.id_ticket && (editedItem.observacao_fechamento_externa || (showInternalObservationEffective && editedItem.observacao_fechamento_interna))">
                    <v-card-title class="text-h6 pa-0 mb-3">
                        <v-icon left color="primary">mdi-close-circle</v-icon>
                        {{ $t('ticket.closingObservations') || 'Observações de fechamento' }}
                    </v-card-title>
                    <v-divider class="mb-3"></v-divider>
                    <div class="text-body-2">
                        <div class="d-flex align-start mb-2" v-if="editedItem.observacao_fechamento_externa">
                            <span class="text-caption grey--text mr-2">{{ $t('ticket.tasks.externalObservation') || 'Obs. externa' }}:</span>
                            <span>{{ editedItem.observacao_fechamento_externa }}</span>
                        </div>
                        <div class="d-flex align-start" v-if="showInternalObservationEffective && editedItem.observacao_fechamento_interna">
                            <span class="text-caption grey--text mr-2">{{ $t('ticket.tasks.internalObservation') || 'Obs. interna' }}:</span>
                            <span>{{ editedItem.observacao_fechamento_interna }}</span>
                        </div>
                    </div>
                </v-card>

                <v-row v-if="showComments">
                    <v-col cols="12">
                        <v-card class="pa-4 mb-4" outlined height="300px" style="overflow-y: auto;">
                            <v-card-title class="text-h6 pa-0 mb-3">
                                <v-icon left color="primary">mdi-comment-multiple</v-icon>
                                {{ $t('ticket.comments.history') }}
                            </v-card-title>
                            <v-divider class="mb-3"></v-divider>
                            <div v-if="comentarios.length">
                                <div 
                                    v-for="(comentario, i) in comentarios"
                                    :key="`COMENTARIO-${comentario.id_ticket}.${i}`"
                                    class="mb-3"
                                >
                                    <v-row no-gutters>
                                        <v-col>
                                            <div class="text-subtitle-2 font-weight-bold">
                                                {{ comentario.nome_usuario_comentario }}
                                            </div>
                                            <div class="text-caption grey--text">
                                                {{ comentario.dt_inclusao_extenso }}
                                            </div>
                                            <v-chip v-if="Number(comentario.interno) === 1" x-small class="mt-1" color="warning" outlined>
                                                {{ $t('ticketSuporteFluxo.internoTag') }}
                                            </v-chip>
                                            <div class="mt-1 ticket-detail-corpo">{{ comentario.corpo }}</div>
                                        </v-col>
                                    </v-row>
                                    <v-divider class="my-2"></v-divider>
                                </div>
                            </div>
                            
                            <div v-else class="text-subtitle-2 grey--text">
                                <v-icon color="primary" class="mr-2">mdi-comment-outline</v-icon>
                                {{ $t('ticket.comments.noComments') }}
                            </div>
                        </v-card>
                        <v-card class="pa-4" outlined v-if="!isViewerOnly">
                            <v-card-title class="text-h6 pa-0 mb-3">
                                <v-icon left color="primary">mdi-reply</v-icon>
                                {{ $t('ticket.comments.add') }}
                            </v-card-title>
                            <v-divider class="mb-3"></v-divider>
                            <v-form ref="formComentario" v-model="formComentarioValidado">
                                <v-textarea 
                                    :value="editedComentario.corpo"
                                    @input="$emit('update:comment', $event)"
                                    :label="$t('ticket.comments.enterAnswer')"
                                    :disabled="commentsWriteDisabled || loadingComentario" 
                                    auto-grow 
                                    rows="4"
                                    outlined 
                                    clearable 
                                    counter="1000"
                                    :placeholder="$t('ticket.comments.placeholder')"
                                    :rules="[
                                        (v) => !!v || $t('ticket.comments.validation.required'),
                                        (v) => !v || v.length <= 1000 || $t('ticket.comments.validation.maxLength')
                                    ]"
                                />
                            </v-form>
                            <v-checkbox
                                v-if="podeComentarInterno"
                                :input-value="editedComentario.interno"
                                @change="$emit('update:commentInterno', !!$event)"
                                :label="$t('ticketSuporteFluxo.comentarioInterno')"
                                dense
                                hide-details
                            ></v-checkbox>
                            <div class="d-flex justify-end mt-3">
                                <v-btn
                                    color="primary"
                                    dark
                                    @click="$emit('send-comment')"
                                    :loading="loadingComentario"
                                    :disabled="commentsWriteDisabled || loadingComentario || !editedComentario.corpo || editedComentario.corpo.trim() === ''"
                                >
                                    <v-icon left>mdi-send</v-icon>
                                    {{ $t('button.sendAnswer') }}
                                </v-btn>
                            </div>
                        </v-card>
                        <v-card v-else-if="isViewerOnly || readOnlyTicketDetail" class="pa-4" outlined>
                            <v-alert type="info" dense outlined>{{ $t('ticket.comments.viewerOnly') }}</v-alert>
                        </v-card>
                    </v-col>
                </v-row>
                </div>
            </v-tab-item>
            <v-tab-item v-if="podeVerHistorico" value="tab-historico">
                <ticket-historico-tab :id-ticket="editedItem.id_ticket"></ticket-historico-tab>
            </v-tab-item>
        </v-tabs-items>

        <!-- Equipment select QR Scanner Dialog (detail view) -->
        <v-dialog v-model="equipmentQrScannerDialog" max-width="800" persistent>
            <v-card>
                <v-toolbar dark color="primary">
                    <v-toolbar-title>{{ $t('equipment.qrScanner.title') || 'Ler QR Code - Equipamento' }}</v-toolbar-title>
                    <v-spacer></v-spacer>
                    <v-btn icon dark @click="closeEquipmentQRScanner">
                        <v-icon>mdi-close</v-icon>
                    </v-btn>
                </v-toolbar>
                <v-card-text>
                    <v-container>
                        <v-alert v-if="equipmentQrScannerError" type="error" dense class="mb-4">
                            {{ equipmentQrScannerError }}
                        </v-alert>
                        <v-alert type="info" dense class="mb-4">
                            {{ $t('equipmentChecklist.qrScannerInstructions') || $t('equipment.qrScanner.instructions') }}
                        </v-alert>
                        <div style="position: relative; width: 100%; max-width: 600px; margin: 0 auto;">
                            <qrcode-stream
                                v-if="equipmentQrScannerDialog"
                                @decode="onEquipmentQRDecode"
                                @init="onEquipmentQRInit"
                                :camera="equipmentQrCamera"
                                style="width: 100%; border-radius: 8px;"
                            ></qrcode-stream>
                        </div>
                    </v-container>
                </v-card-text>
                <v-card-actions>
                    <v-spacer></v-spacer>
                    <v-btn color="grey" text @click="closeEquipmentQRScanner">
                        {{ $t('button.cancel') }}
                    </v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>

        <!-- Pré-candidatos CSV/Excel update processing dialog -->
        <v-dialog
            :value="precCsvUploading"
            persistent
            max-width="420"
            content-class="prec-csv-processing-dialog"
        >
            <v-card>
                <v-card-text class="text-center pt-6 pb-4">
                    <h3 class="primary--text mb-4">{{ $t('system.messages.processingFile') }}</h3>
                    <v-progress-circular
                        indeterminate
                        :size="48"
                        :width="5"
                        color="primary"
                    />
                </v-card-text>
            </v-card>
        </v-dialog>

        <!-- Pré-candidatos CSV update result / confirm dialog -->
        <v-dialog
            v-model="precCsvResultDialog"
            persistent
            max-width="1280"
            scrollable
            content-class="prec-csv-result-dialog"
        >
            <v-card>
                <v-card-title class="headline">
                    {{ $t('ticket.detail.precandidatos.resultDialog.title') }}
                </v-card-title>
                <v-card-text>
                    <p class="body-2 mb-2">
                        {{ $t('ticket.detail.precandidatos.resultDialog.summary', {
                            total: (precCsvPreviewRows || []).length,
                            tipos: (precCsvPreviewTipos || []).length,
                        }) }}
                    </p>
                    <v-alert
                        v-if="(precCsvPreviewTipos || []).length"
                        type="info"
                        dense
                        text
                        class="mb-3"
                    >
                        {{ $t('ticket.detail.precandidatos.resultDialog.tiposDetected', {
                            nomes: (precCsvPreviewTipos || []).join(', '),
                        }) }}
                    </v-alert>
                    <v-data-table
                        dense
                        item-key="id_pre_candidato"
                        :headers="precCsvPreviewHeaders"
                        :items="precCsvPreviewRows"
                        :items-per-page="15"
                        class="elevation-1"
                        :footer-props="{ itemsPerPageOptions: [10, 15, 25, 50] }"
                    />
                </v-card-text>
                <v-card-actions>
                    <v-btn text :disabled="precCsvSaving" @click="cancelPrecandidatosCsvPreview">
                        {{ $t('button.cancel') }}
                    </v-btn>
                    <v-spacer />
                    <v-btn
                        color="primary"
                        :loading="precCsvSaving"
                        :disabled="!(precCsvPreviewRows || []).length"
                        @click="confirmPrecandidatosCsvPreview"
                    >
                        {{ $t('button.save') }}
                    </v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>
    </v-container>
</template>

<script>
import apiController from "@/utils/apiController";
import ZonaRiscoChip from "@/components/Areas/ZonaRiscoChip.vue";
import { ApiBaseUrl } from "@/constants";
import { QrcodeStream } from "vue-qrcode-reader";
import EventBus from "../../utils/eventBus";
import { ticketAssigneesDisplayLabel } from "@/utils/ticketAssignees";
import { filterTicketStatusesByPerfil } from "@/utils/ticketStatusPerfilFilter";
import { STATUS_PENDENTE, camposObrigatoriosFaltantes, campoPendenteLabelKey } from "@/utils/ticketPendente";
import { rotuloCatalogo } from "@/utils/ticketCatalogoLabel";
import TicketSuporteFluxoCard from "./TicketSuporteFluxoCard.vue";
import TicketHistoricoTab from "./TicketHistoricoTab.vue";
import MultiplaEscolha from "@/components/Selects/MultiplaEscolha.vue";

/** Faixas do SLA manual; as mesmas do SLA calculado (Tickets.SQL_SLA_FAIXA). */
const SLA_MANUAL_FAIXAS = ['10H', '17H', '24H', '48H'];

function turnoPrecandidatoValSlotName(id_turno) {
    return "precTurnoVal_" + String(id_turno);
}

function turnoTipoPrecValSlotName(id_tipo_turno_precandidato) {
    return "precTipoTurnoVal_" + String(id_tipo_turno_precandidato);
}

/** Same weekly window on all configured weekdays → single inicio/fim; else empty. */
function getTurnoHorarioSemanal(turno) {
    if (!turno) {
        return { inicio: "", fim: "" };
    }
    const dayKeys = [
        ["horario_inicio_segunda", "horario_fim_segunda"],
        ["horario_inicio_terca", "horario_fim_terca"],
        ["horario_inicio_quarta", "horario_fim_quarta"],
        ["horario_inicio_quinta", "horario_fim_quinta"],
        ["horario_inicio_sexta", "horario_fim_sexta"],
        ["horario_inicio_sabado", "horario_fim_sabado"],
        ["horario_inicio_domingo", "horario_fim_domingo"]];
    const sigs = [];
    for (let i = 0; i < dayKeys.length; i++) {
        const ki = dayKeys[i][0];
        const kf = dayKeys[i][1];
        const ini = turno[ki];
        const fim = turno[kf];
        if (ini != null && String(ini).trim() !== "" && fim != null && String(fim).trim() !== "") {
            sigs.push(String(ini).trim() + "\u001f" + String(fim).trim());
        }
    }
    if (sigs.length === 0) {
        return { inicio: "", fim: "" };
    }
    const first = sigs[0];
    for (let j = 1; j < sigs.length; j++) {
        if (sigs[j] !== first) {
            return { inicio: "", fim: "" };
        }
    }
    const parts = first.split("\u001f");
    return { inicio: parts[0] || "", fim: parts[1] || "" };
}

function precParseValidacaoPorTurnoMap(raw) {
    if (raw == null || raw === "") {
        return {};
    }
    if (typeof raw === "object" && raw !== null && !Array.isArray(raw)) {
        try {
            return JSON.parse(JSON.stringify(raw));
        } catch (e) {
            return {};
        }
    }
    try {
        const o = JSON.parse(String(raw));
        return typeof o === "object" && o !== null && !Array.isArray(o) ? o : {};
    } catch (e2) {
        return {};
    }
}

const TICKET_TYPE_SOLICITACAO_CANDIDATOS_AGENCIA = "SOLICITAÇÃO DE CANDIDATOS (AGÊNCIA)";
const TICKET_TYPE_SUPORTE_SISTEMA = "Suporte Sistema (MerusTraker)";

const SUPORTE_SISTEMA_CATEGORIA_I18N = {
    cadastro: 'ticketSuporteSistema.categorias.cadastro',
    aplicacao: 'ticketSuporteSistema.categorias.aplicacao',
    relatorio: 'ticketSuporteSistema.categorias.relatorio',
    configuracao: 'ticketSuporteSistema.categorias.configuracao',
};

function isSuporteSistemaTipoNome(nome) {
    const normalized = String(nome || '').trim().toLowerCase();
    return normalized === TICKET_TYPE_SUPORTE_SISTEMA.toLowerCase()
        || normalized === 'suporte sistema (merustraker)';
}

export default {
    name: 'TicketDetailView',
    components: { QrcodeStream, ZonaRiscoChip, MultiplaEscolha, TicketSuporteFluxoCard, TicketHistoricoTab },
    props: {
        podeVerHistorico: {
            type: Boolean,
            default: false
        },
        usuarioAtual: {
            type: Object,
            default: null
        },
        editedItem: {
            type: Object,
            required: true
        },
        status: {
            type: Array,
            default: () => []
        },
        showStatus: {
            type: Boolean,
            default: false
        },
        equipes: {
            type: Array,
            default: () => []
        },
        usuariosPorEquipe: {
            type: Array,
            default: () => []
        },
        checklistSelecionado: {
            type: Object,
            default: null
        },
        editChecklist: {
            type: Boolean,
            default: true
        },
        checklistItem: {
            type: Object,
            default: () => ({})
        },
        checklistValoresCampos: {
            type: Object,
            default: () => ({})
        },
        checklistValoresEditedBy: {
            type: Object,
            default: () => ({})
        },
        checklistNome: {
            type: String,
            default: null
        },
        equipamentosDisplayText: {
            type: String,
            default: '-'
        },
        veiculosDisplayText: {
            type: String,
            default: '-'
        },
        comentarios: {
            type: Array,
            default: () => []
        },
        editedComentario: {
            type: Object,
            default: () => ({ corpo: '' })
        },
        loadingChecklistDetails: {
            type: Boolean,
            default: false
        },
        loadingComentario: {
            type: Boolean,
            default: false
        },
        isTicketClosed: {
            type: Boolean,
            default: false
        },
        checklistTimeProps: {
            type: Object,
            default: () => ({ useSeconds: true })
        },
        checklistTextFieldProps: {
            type: Object,
            default: () => ({ prependIcon: "mdi-calendar" })
        },
        // Visibility props
        showAssignedUser: {
            type: Boolean,
            default: true
        },
        showSchedule: {
            type: Boolean,
            default: true
        },
        showChecklist: {
            type: Boolean,
            default: true
        },
        showEquipment: {
            type: Boolean,
            default: true
        },
        showVehicle: {
            type: Boolean,
            default: true
        },
        showStatusChange: {
            type: Boolean,
            default: true
        },
        showStatusAtribuido: {
            type: Boolean,
            default: false
        },
        showStatusDispatcher: {
            type: Boolean,
            default: false
        },
        showStatusAtribuidoChange: {
            type: Boolean,
            default: false
        },
        showStatusDispatcherChange: {
            type: Boolean,
            default: false
        },
        /** Hide dispatcher status editor (e.g. technician / colaborador screen) */
        lockDispatcherStatusToReadonly: {
            type: Boolean,
            default: false
        },
        /** Hide technician “change status” dialog on info tab (e.g. dispatcher screen) */
        lockTaskStatusDialog: {
            type: Boolean,
            default: false
        },
        /** Allow editing task row status in Tasks tab (assigned technician) */
        canEditInlineTaskStatus: {
            type: Boolean,
            default: false
        },
        /** Dispatcher/supervisor: can set task to closing statuses (Tasks tab) */
        allowDispatcherCloseTask: {
            type: Boolean,
            default: false
        },
        currentUserId: {
            type: [Number, String],
            default: null
        },
        showSelectionTreeEdit: {
            type: Boolean,
            default: false
        },
        tiposTickets: {
            type: Array,
            default: () => []
        },
        familiasAtendimento: {
            type: Array,
            default: () => []
        },
        tiposAtendimento: {
            type: Array,
            default: () => []
        },
        servicos: {
            type: Array,
            default: () => []
        },
        /** Tipos de solicitação do tipo do ticket (Tipo de problema no suporte sistema). */
        tiposSolicitacao: {
            type: Array,
            default: () => []
        },
        modulosSistema: {
            type: Array,
            default: () => []
        },
        showUserAssignment: {
            type: Boolean,
            default: true
        },
        showTeam: {
            type: Boolean,
            default: true
        },
        showScheduleEdit: {
            type: Boolean,
            default: true
        },
        showExpectedFinalizationEdit: {
            type: Boolean,
            default: false
        },
        expectedFinalizationDate: {
            type: [Date, String],
            default: null
        },
        showChecklistSection: {
            type: Boolean,
            default: true
        },
        showComments: {
            type: Boolean,
            default: true
        },
        // Vehicle and equipment editing
        vehicleEquipmentPairs: {
            type: Array,
            default: () => []
        },
        empresa: {
            type: [Number, String],
            default: null
        },
        equipamentosFiltradosPorEmpresa: {
            type: Array,
            default: () => []
        },
        veiculosFiltradosPorEmpresa: {
            type: Array,
            default: () => []
        },
        loadingEquipamentos: {
            type: Boolean,
            default: false
        },
        loadingVeiculos: {
            type: Boolean,
            default: false
        },
        vehicleEquipmentPairsValid: {
            type: Boolean,
            default: true
        },
        getEquipamentoDisplayTextById: {
            type: Function,
            default: null
        },
        initialDetailTab: {
            type: String,
            default: null
        },
        /** When set (assist flow), prefill change-status dialog start time */
        assistStatusDtInicio: {
            type: [Date, String, Number],
            default: null
        },
        /** Show “Alterar status” CTA on Tasks tab when checklist is fulfilled (assist / mobile) */
        showTasksTabStatusCta: {
            type: Boolean,
            default: false
        },
        token: {
            type: String,
            default: null
        },
        cancelButton: {
            type: Boolean,
            default: true
        },
        /** When set, overrides individual show* props: only fields in this array are shown in the detail. From config ticket detail fields. */
        visibleDetailFields: {
            type: Array,
            default: null
        },
        /** When true (e.g. perfil AgenciaEmprego), hide ticket editors for checklist / status / pré-candidatos; comments remain allowed via parent. */
        readOnlyTicketDetail: {
            type: Boolean,
            default: false
        },
        /** When true, hide "atualizar via CSV" and "finalizar alteração" (pré-candidatos) buttons. */
        hidePrecandidatosWriteActions: {
            type: Boolean,
            default: false
        },
        /** When true, hide internal observation fields (cliente / criados screens). */
        hideInternalObservation: {
            type: Boolean,
            default: false
        }
    },
    data() {
        return {
            detailActiveTab: 'tab-info',
            formComentarioValidado: false,
            ticketTasks: [],
            loadingTasks: false,
            ticketAnexos: [],
            loadingAnexos: false,
            suporteSistemaAnexos: [],
            loadingSuporteAnexos: false,
            equipmentQrScannerDialog: false,
            equipmentQrScannerError: null,
            equipmentQrCamera: 'auto',
            /** When true, equipment/kit autocomplete lists only items linked to selectedVehicleId */
            filterEquipamentosByVehicle: false,
            assignUserDialog: false,
            assignDialogDescricao: '',
            assignDialogUserIds: [],
            assignDialogDtPrevistaFim: null,
            changeStatusAtribuidoDialog: false,
            changeStatusDialogIdStatus: null,
            changeStatusDialogDtInicio: null,
            changeStatusDialogDtFim: null,
            changeStatusDialogObservacaoExterna: '',
            changeStatusDialogObservacaoInterna: '',
            changeStatusDialogAnexos: [],
            assignDialogAnexos: [],
            uploadingStatusAnexos: false,
            uploadingAssignAnexos: false,
            statusTasksSelect: [],
            savingTaskSuffix: null,
            precandidatosList: [],
            loadingPrecandidatos: false,
            precCsvDownloading: false,
            precCsvUploading: false,
            precCsvSaving: false,
            precCsvResultDialog: false,
            precCsvPendingFile: null,
            precCsvPreviewRows: [],
            precCsvPreviewTipos: [],
            precSavingId: null,
            precTurnosList: [],
            precTiposTurnosPrecandidatosList: [],
            precColunasExtras: [],
            precLinhasExtras: [],
            precDownloadExpirado: false,
            precDtLimiteDownload: null,
            precSituacoesList: [],
        };
    },
    watch: {
        allowDispatcherCloseTask() {
            if (this.editedItem && this.editedItem.id_ticket) {
                this.fetchTaskStatuses();
            }
        },
        'editedItem.id_ticket': {
            handler(id) {
                if (id) {
                    this.fetchTicketTasks(id);
                    this.fetchTicketAnexos(id);
                    if (this.isSuporteSistemaTicket) {
                        this.fetchSuporteSistemaAnexos(id);
                    } else {
                        this.suporteSistemaAnexos = [];
                    }
                    this.fetchTaskStatuses();
                    this.fetchPrecandidatosPorTicket(id);
                    this.clearPrecandidatosCsvPreview();
                } else {
                    this.ticketTasks = [];
                    this.ticketAnexos = [];
                    this.suporteSistemaAnexos = [];
                    this.statusTasksSelect = [];
                    this.precandidatosList = [];
                    this.precTiposTurnosPrecandidatosList = [];
                    this.clearPrecandidatosCsvPreview();
                    this.precSituacoesList = [];
                }
                this.applyInitialDetailTab();
            },
            immediate: true
        },
        changeStatusDialogDtInicio(val) {
            if (val && this.changeStatusDialogDtFim) {
                const inicio = val instanceof Date ? val.getTime() : new Date(val).getTime();
                const fim = this.changeStatusDialogDtFim instanceof Date ? this.changeStatusDialogDtFim.getTime() : new Date(this.changeStatusDialogDtFim).getTime();
                if (fim < inicio) {
                    this.changeStatusDialogDtFim = null;
                }
            }
        },
        initialDetailTab() {
            this.applyInitialDetailTab();
        },
        showChecklistSectionEffective(val) {
            if (!val && this.detailActiveTab === 'tab-arquivos') {
                this.detailActiveTab = 'tab-info';
            }
            this.applyInitialDetailTab();
        },
        isAgenciaCandidatosTicket() {
            this.applyInitialDetailTab();
        },
        isSuporteSistemaTicket(val) {
            const id = this.editedItem && this.editedItem.id_ticket;
            if (val && id) {
                this.fetchSuporteSistemaAnexos(id);
            } else if (!val) {
                this.suporteSistemaAnexos = [];
            }
            this.applyInitialDetailTab();
        },
    },
    computed: {
        isAgenciaCandidatosTicket() {
            const e = this.editedItem;
            if (!e) return false;
            return String(e.tipo_ticket_extenso || "").trim() === TICKET_TYPE_SOLICITACAO_CANDIDATOS_AGENCIA;
        },
        /** Agency candidate tickets keep comments writable after close. */
        commentsWriteDisabled() {
            if (this.isAgenciaCandidatosTicket) return false;
            return !!this.isTicketClosed;
        },
        /**
         * Agency candidate tickets: prefer recovery email, then login (often the person's email),
         * then stored solicitante_email when it looks like an email; else fall back to nome.
         */
        solicitanteDisplayValue() {
            const e = this.editedItem;
            if (!e) return '';
            if (this.isAgenciaCandidatosTicket) {
                const emailRec = e.email_recuperacao_usuario_inclusao != null
                    ? String(e.email_recuperacao_usuario_inclusao).trim()
                    : '';
                if (emailRec) return emailRec;
                const login = e.login_usuario_inclusao != null
                    ? String(e.login_usuario_inclusao).trim()
                    : '';
                if (login) return login;
                const stored = e.solicitacao_candidatos_solicitante_email != null
                    ? String(e.solicitacao_candidatos_solicitante_email).trim()
                    : '';
                if (stored && stored.includes('@')) return stored;
            }
            const nome = e.nome_usuario_solicitante;
            return nome != null ? String(nome).trim() : '';
        },
        /** Solicitante não escreve comentário interno; o servidor confere de novo. */
        podeComentarInterno() {
            const e = this.editedItem || {};
            const u = this.usuarioAtual;
            return !!u && Number(e.id_usuario_inclusao) !== Number(u.id_usuario);
        },
        isSuporteSistemaTicket() {
            const e = this.editedItem;
            if (!e) return false;
            // O tipo escolhido no card vale antes de salvar: tipo_ticket_extenso só muda depois.
            const tipoRow = (this.tiposTickets || []).find((t) => Number(t.id_tipo_ticket) === Number(e.id_tipo_ticket));
            if (tipoRow) return isSuporteSistemaTipoNome(tipoRow.nome_tipo_ticket);
            if (isSuporteSistemaTipoNome(e.tipo_ticket_extenso)) return true;
            if (e.suporte_sistema_id_modulo != null && String(e.suporte_sistema_id_modulo).trim() !== '') return true;
            if (e.suporte_sistema_nome_modulo != null && String(e.suporte_sistema_nome_modulo).trim() !== '') return true;
            return false;
        },
        suporteSistemaModuloDisplay() {
            const e = this.editedItem || {};
            const nome = e.suporte_sistema_nome_modulo;
            if (nome == null || String(nome).trim() === '') return '-';
            const cat = e.suporte_sistema_categoria_modulo;
            if (cat) {
                const catKey = SUPORTE_SISTEMA_CATEGORIA_I18N[cat];
                const catLabel = catKey ? this.$t(catKey) : cat;
                return `${catLabel} — ${String(nome).trim()}`;
            }
            return String(nome).trim();
        },
        modulosSistemaComGrupo() {
            return (this.modulosSistema || []).map((m) => {
                const catKey = SUPORTE_SISTEMA_CATEGORIA_I18N[m.categoria];
                const catLabel = catKey ? this.$t(catKey) : m.categoria;
                return { ...m, label: catLabel ? `${catLabel} — ${m.nome_modulo}` : m.nome_modulo };
            });
        },
        chaveFamiliaSelecionada() {
            const id = (this.editedItem || {}).id_familia_atendimento;
            const familia = (this.familiasAtendimento || []).find((f) => Number(f.id_familia_atendimento) === Number(id));
            return familia ? familia.chave_i18n : null;
        },
        /** Módulo do sistema só se aplica ao tipo de caso Software (a API aplica a mesma regra). */
        familiaSoftware() {
            return this.chaveFamiliaSelecionada === 'familia.software';
        },
        familiaSemModulo() {
            const id = (this.editedItem || {}).id_familia_atendimento;
            return id != null && id !== '' && !this.familiaSoftware;
        },
        /** Módulo editado no card (id_modulo_sistema) ou o gravado na extensão do suporte. */
        moduloSistemaSelectValue() {
            const e = this.editedItem || {};
            const raw = Object.prototype.hasOwnProperty.call(e, 'id_modulo_sistema')
                ? e.id_modulo_sistema
                : e.suporte_sistema_id_modulo;
            if (raw == null || raw === '') return null;
            const n = Number(raw);
            return Number.isFinite(n) ? n : raw;
        },
        /** "Compartilhar com": array editado no card ou o CSV do detalhe. */
        visualizadoresSelectValue() {
            const e = this.editedItem || {};
            if (Array.isArray(e.id_usuarios_visualizadores)) return e.id_usuarios_visualizadores;
            const csv = e.id_usuarios_visualizadores_csv;
            if (csv == null || String(csv).trim() === '') return [];
            return String(csv).split(',').map((s) => Number(s.trim())).filter((n) => Number.isFinite(n));
        },
        /** Itens já selecionados com nome, para os chips aparecerem antes da busca lazy. */
        visualizadoresIniciais() {
            const e = this.editedItem || {};
            const csv = e.id_usuarios_visualizadores_csv;
            if (csv == null || String(csv).trim() === '') return [];
            const ids = String(csv).split(',').map((s) => Number(s.trim()));
            const nomes = String(e.nomes_usuarios_visualizadores || '').split(', ');
            return ids
                .map((id, i) => ({ id_usuario: id, nome_usuario: nomes[i] || String(id) }))
                .filter((u) => Number.isFinite(u.id_usuario));
        },
        /** Ticket ainda Pendente (status do coordenador pelo nome; o id varia por instalação). */
        isTicketPendente() {
            const e = this.editedItem || {};
            const id = e.id_status_dispatcher;
            const row = id != null && id !== ''
                ? (this.status || []).find((s) => Number(s.id_status_ticket) === Number(id))
                : null;
            const nome = row ? row.status_ticket : e.status_dispatcher_extenso;
            return String(nome || '').trim() === STATUS_PENDENTE;
        },
        camposPendentesLabels() {
            if (!this.isTicketPendente) return [];
            const e = this.editedItem || {};
            const tipoRow = (this.tiposTickets || []).find((t) => Number(t.id_tipo_ticket) === Number(e.id_tipo_ticket));
            const nomeTipo = tipoRow ? tipoRow.nome_tipo_ticket : e.tipo_ticket_extenso;
            return camposObrigatoriosFaltantes(e, nomeTipo)
                .map((campo) => campoPendenteLabelKey(campo, this.isSuporteSistemaTicket))
                .filter(Boolean)
                .map((key) => this.$t(key));
        },
        /** Generic service hierarchy (família/serviço/equipamentos) — hidden for agency and suporte sistema. */
        showGenericTicketHierarchy() {
            return !this.isAgenciaCandidatosTicket && !this.isSuporteSistemaTicket;
        },
        /** Pré-candidatos: respect read-only (e.g. perfil AgenciaEmprego) before allowing solicitação-candidatos edits. */
        /** Prazo de download dos CSVs após a finalização (Configurações); vazio quando não há limite. */
        precDownloadPrazoHint() {
            if (!this.precDtLimiteDownload) return '';
            return this.precDownloadExpirado
                ? this.$t('ticket.detail.precandidatos.downloadExpirado', { data: this.precDtLimiteDownload })
                : this.$t('ticket.detail.precandidatos.downloadDisponivelAte', { data: this.precDtLimiteDownload });
        },
        precCandidatosWriteDisabled() {
            if (this.isTicketClosed) return true;
            if (this.readOnlyTicketDetail) return true;
            if (this.isAgenciaCandidatosTicket) return false;
            return false;
        },
        /** Single status line for agency candidatos tickets (dispatcher has priority). */
        agenciaCandidatosStatusExtenso() {
            const e = this.editedItem;
            if (!e) return '';
            const d = e.status_dispatcher_extenso;
            const a = e.status_atribuido_extenso;
            const s = e.status_extenso;
            if (d != null && String(d).trim() !== '') return String(d).trim();
            if (a != null && String(a).trim() !== '') return String(a).trim();
            if (s != null && String(s).trim() !== '') return String(s).trim();
            return '';
        },
        showAgenciaCandidatosUnifiedStatusReadonly() {
            if (!this.isAgenciaCandidatosTicket) return false;
            return (
                this.showStatusEffective ||
                this.showStatusAtribuidoEffective ||
                this.showStatusDispatcherEffective
            );
        },
        showAgenciaCandidatosUnidadesInfo() {
            const e = this.editedItem;
            if (!e) return false;
            const label = e.solicitacao_candidatos_unidade_label;
            if (label != null && String(label).trim() !== '') return true;
            const desc = e.solicitacao_candidatos_unidade_descricao;
            if (desc != null && String(desc).trim() !== '') return true;
            const ids = e.solicitacao_candidatos_id_unidades;
            if (Array.isArray(ids) && ids.length > 0) return true;
            const one = e.solicitacao_candidatos_id_unidade;
            return one != null && String(one).trim() !== '';
        },
        showAgenciaCandidatosAgenciaInfo() {
            const e = this.editedItem;
            if (!e) return false;
            const nome = e.solicitacao_candidatos_nome_agencia;
            if (nome != null && String(nome).trim() !== '') return true;
            const id = e.solicitacao_candidatos_id_agencia;
            return id != null && String(id).trim() !== '';
        },
        agenciaCandidatosAgenciaDisplay() {
            const e = this.editedItem;
            if (!e) return '-';
            const nome = e.solicitacao_candidatos_nome_agencia;
            if (nome != null && String(nome).trim() !== '') return String(nome).trim();
            const id = e.solicitacao_candidatos_id_agencia;
            if (id != null && String(id).trim() !== '') return String(id).trim();
            return '-';
        },
        showAgenciaCandidatosAreaApoioInfo() {
            const e = this.editedItem;
            if (!e) return false;
            const id = e.solicitacao_candidatos_id_area_apoio;
            if (id != null && String(id).trim() !== '') return true;
            const nome = e.solicitacao_candidatos_nome_area_apoio;
            const cod = e.solicitacao_candidatos_cod_area_apoio;
            return (nome != null && String(nome).trim() !== '') || (cod != null && String(cod).trim() !== '');
        },
        agenciaCandidatosAreasDisplay() {
            const e = this.editedItem;
            const label = e && e.solicitacao_candidatos_areas_label;
            return label != null && String(label).trim() !== '' ? String(label).trim() : '';
        },
        agenciaCandidatosUnidadesDisplay() {
            const e = this.editedItem;
            if (!e) return '-';
            const label = e.solicitacao_candidatos_unidade_label;
            if (label != null && String(label).trim() !== '') {
                return String(label).trim();
            }
            const desc = e.solicitacao_candidatos_unidade_descricao;
            if (desc != null && String(desc).trim() !== '') {
                return String(desc).trim();
            }
            const ids = e.solicitacao_candidatos_id_unidades;
            if (Array.isArray(ids) && ids.length > 0) {
                return ids.map((id) => this.$t('ticket.information.businessUnitFallback', { id })).join(', ');
            }
            const one = e.solicitacao_candidatos_id_unidade;
            if (one != null && String(one).trim() !== '') {
                return this.$t('ticket.information.businessUnitFallback', { id: one });
            }
            return '-';
        },
        agenciaCandidatosAreaApoioDisplay() {
            const e = this.editedItem;
            if (!e) return '-';
            const cod = e.solicitacao_candidatos_cod_area_apoio != null ? String(e.solicitacao_candidatos_cod_area_apoio).trim() : '';
            const nome = e.solicitacao_candidatos_nome_area_apoio != null ? String(e.solicitacao_candidatos_nome_area_apoio).trim() : '';
            if (cod && nome) return `${cod} – ${nome}`;
            if (nome) return nome;
            if (cod) return cod;
            return '-';
        },
        agenciaCandidatosUnidadesCodesDisplay() {
            const e = this.editedItem;
            if (!e) return '';
            const c = e.solicitacao_candidatos_cod_unidade;
            return c != null && String(c).trim() !== '' ? String(c).trim() : '';
        },
        agenciaCandidatosUploadedFileName() {
            const e = this.editedItem;
            const orig = e && e.solicitacao_candidatos_nome_original;
            if (orig != null && String(orig).trim() !== "") {
                return String(orig).trim();
            }
            const p = e && e.solicitacao_candidatos_arquivo;
            if (!p || typeof p !== "string") return "";
            const norm = p.replace(/\\/g, "/");
            const parts = norm.split("/").filter(Boolean);
            return parts.length ? parts[parts.length - 1] : String(p);
        },
        /** Stored value may be basename only (new) or legacy full/static path. */
        agenciaCandidatosArquivoHref() {
            const p = this.editedItem && this.editedItem.solicitacao_candidatos_arquivo;
            if (!p || typeof p !== "string") return "";
            const s = p.trim();
            if (s.includes("/") || s.includes("\\") || s.startsWith("static")) {
                return this.getPhotoUrl(s);
            }
            return this.getPhotoUrl("uploads/candidatos/" + s);
        },
        detailSecondaryTabIcon() {
            if (this.isAgenciaCandidatosTicket || this.isSuporteSistemaTicket) {
                return 'mdi-folder-multiple-image';
            }
            return 'mdi-clipboard-check';
        },
        checklistTabDisplayName() {
            if (this.isAgenciaCandidatosTicket || this.isSuporteSistemaTicket) {
                const n = "Arquivos";
                return n && String(n).trim() ? String(n).trim() : this.$t("ticket.checklist.checklist");
            }
            return this.$t("ticket.checklist.checklist");
        },
        precandidatosHeaders() {
            const base = [
                { text: this.$t('ticket.detail.precandidatos.cols.linha'), value: 'linha_arquivo', width: '72px' },
                { text: this.$t('ticket.detail.precandidatos.cols.nome'), value: 'nome_completo' },
                { text: this.$t('ticket.detail.precandidatos.cols.rua'), value: 'rua' },
                { text: this.$t('ticket.detail.precandidatos.cols.numero'), value: 'numero' },
                { text: this.$t('ticket.detail.precandidatos.cols.bairro'), value: 'bairro' },
                { text: this.$t('ticket.detail.precandidatos.cols.cidade'), value: 'cidade' },
                { text: this.$t('ticket.detail.precandidatos.cols.cep'), value: 'cep' },
                { text: this.$t('ticket.detail.precandidatos.cols.turno'), value: 'turno_desejado' },
                { text: this.$t('area.riskZone'), value: 'zonas_risco_label', sortable: false, width: '120px' }];
            const validacaoCol = {
                text: this.$t('validationSituation.headers.situation'),
                value: 'validacao',
                width: '220px',
            };
            if ((this.precTiposTurnosPrecSorted || []).length > 0) {
                const tipoCols = this.precTiposTurnosPrecSorted.map((tt) => ({
                    text: String(tt.nome || '').trim() || `#${tt.id_tipo_turno_precandidato}`,
                    value: turnoTipoPrecValSlotName(tt.id_tipo_turno_precandidato),
                    sortable: false,
                    width: '160px',
                    align: 'left',
                }));
                return base.concat([validacaoCol], tipoCols);
            }
            const turnoCols = (this.precTurnosSorted || []).map((t) => ({
                text: this.precTurnoValidationHeader(t),
                value: turnoPrecandidatoValSlotName(t.id_turno),
                sortable: false,
                width: '200px',
                align: 'center',
            }));
            return base.concat(turnoCols, [validacaoCol]);
        },
        precCsvPreviewHeaders() {
            const base = [
                { text: this.$t('ticket.detail.precandidatos.cols.linha'), value: 'linha_arquivo', width: '72px' },
                { text: 'ID', value: 'id_pre_candidato', width: '90px' },
                { text: this.$t('ticket.detail.precandidatos.cols.nome'), value: 'nome_completo' },
                { text: this.$t('ticket.detail.precandidatos.cols.bairro'), value: 'bairro' },
                { text: this.$t('ticket.detail.precandidatos.cols.cidade'), value: 'cidade' },
                { text: this.$t('ticket.detail.precandidatos.cols.cep'), value: 'cep' },
                { text: this.$t('ticket.detail.precandidatos.cols.turno'), value: 'turno_desejado' },
            ];
            const tipoCols = (this.precCsvPreviewTipos || []).map((nome, i) => ({
                text: String(nome),
                value: 'precCsvTipo_' + i,
                sortable: false,
                width: '140px',
            }));
            return base.concat(tipoCols);
        },
        /** Sem pendentes na tabela parcial e com colunas na grade principal. */
        precExtrasFinalizadas() {
            return !(this.precExtrasItems || []).length && (this.precTiposTurnosPrecSorted || []).length > 0;
        },
        precExtrasHeaders() {
            const ids = [
                { text: 'ID', value: 'id_pre_candidato', width: '90px' },
                { text: this.$t('ticket.detail.precandidatos.cols.linha'), value: 'linha_arquivo', width: '72px' },
                { text: this.$t('ticket.detail.precandidatos.cols.nome'), value: 'nome_completo' },
                { text: this.$t('validationSituation.headers.situation'), value: 'validacao', width: '220px', sortable: false },
            ];
            const extras = (this.precColunasExtras || []).map((c) => ({
                text: String(c.nome || ''),
                value: 'extra_' + c.id_coluna,
                sortable: false,
            }));
            return ids.concat(extras);
        },
        /**
         * Colunas ausentes para a linha ficam null (celula vazia). `_base` é a linha da grade principal:
         * a situação de validação editada aqui é gravada nela (mesmo fluxo da tabela normal).
         */
        precExtrasItems() {
            const basePorId = {};
            (this.precandidatosList || []).forEach((r) => { basePorId[String(r.id_pre_candidato)] = r; });
            return (this.precLinhasExtras || []).map((l) => {
                const base = basePorId[String(l.id_pre_candidato)] || l;
                const item = {
                    _base: base,
                    id_pre_candidato: l.id_pre_candidato,
                    linha_arquivo: l.linha_arquivo,
                    nome_completo: l.nome_completo,
                    validacao: base.validacao,
                };
                (this.precColunasExtras || []).forEach((c) => {
                    const v = l.valores ? l.valores[String(c.id_coluna)] : null;
                    item['extra_' + c.id_coluna] = v === undefined ? null : v;
                });
                return item;
            });
        },
        precTiposTurnosPrecSorted() {
            return (this.precTiposTurnosPrecandidatosList || [])
                .slice()
                .sort((a, b) => {
                    const oa = Number(a.ordem);
                    const ob = Number(b.ordem);
                    if (Number.isFinite(oa) && Number.isFinite(ob) && oa !== ob) {
                        return oa - ob;
                    }
                    return Number(a.id_tipo_turno_precandidato) - Number(b.id_tipo_turno_precandidato);
                });
        },
        precTurnosSorted() {
            const base = (this.precTurnosList || [])
                .slice()
                .sort((a, b) => Number(a.id_turno) - Number(b.id_turno));
            const ids = this.precUnidadeIds;
            if (!ids.length) {
                return base;
            }
            return base.filter(
                (t) => !t.id_unidade || ids.includes(Number(t.id_unidade))
            );
        },
        precUnidadeIds() {
            const e = this.editedItem;
            if (!e) return [];
            const raw = Array.isArray(e.solicitacao_candidatos_id_unidades) && e.solicitacao_candidatos_id_unidades.length
                ? e.solicitacao_candidatos_id_unidades
                : (e.solicitacao_candidatos_id_unidade != null && String(e.solicitacao_candidatos_id_unidade).trim() !== ''
                    ? [e.solicitacao_candidatos_id_unidade]
                    : []);
            return [...new Set(
                raw.map((id) => Number(id)).filter((id) => !Number.isNaN(id) && id !== -1)
            )];
        },
        /** Endereço 0..4 for per-turn JSON (same labels as CRUDCandidatos.labelForValidoCode). */
        precEnderecoPorTurnoSelectItems() {
            return [0, 1, 2, 3, 4].map((value) => ({
                value,
                text: this.precSituacaoLabelForValido(value),
            }));
        },
        useDetailFieldsConfig() {
            return Array.isArray(this.visibleDetailFields);
        },
        showEmpresa() {
            return !this.useDetailFieldsConfig ? true : this.visibleDetailFields.includes('empresa');
        },
        showDtInclusao() {
            return !this.useDetailFieldsConfig ? true : this.visibleDetailFields.includes('dt_inclusao');
        },
        showDtPrevistaFim() {
            return !this.useDetailFieldsConfig ? true : this.visibleDetailFields.includes('dt_prevista_fim');
        },
        showDtFinalizacao() {
            return !this.useDetailFieldsConfig ? true : this.visibleDetailFields.includes('dt_finalizacao');
        },
        showNomeUsuarioInclusao() {
            return !this.useDetailFieldsConfig ? true : this.visibleDetailFields.includes('nome_usuario_inclusao');
        },
        showTipoTicket() {
            return !this.useDetailFieldsConfig ? true : this.visibleDetailFields.includes('tipo_ticket');
        },
        showTipoSolicitacao() {
            return !this.useDetailFieldsConfig ? true : this.visibleDetailFields.includes('tipo_solicitacao');
        },
        showFamiliaAtendimento() {
            return !this.useDetailFieldsConfig ? true : this.visibleDetailFields.includes('familia_atendimento');
        },
        showTipoAtendimento() {
            return !this.useDetailFieldsConfig ? true : this.visibleDetailFields.includes('tipo_atendimento');
        },
        showServico() {
            return !this.useDetailFieldsConfig ? true : this.visibleDetailFields.includes('servico');
        },
        showAssignedUserEffective() {
            return !this.useDetailFieldsConfig ? this.showAssignedUser : this.visibleDetailFields.includes('assigned_user');
        },
        hasTicketAssignees() {
            const e = this.editedItem;
            if (!e) return false;
            if (Array.isArray(e.id_usuarios_atribuidos) && e.id_usuarios_atribuidos.length > 0) return true;
            return e.id_usuario_atribuido != null && e.id_usuario_atribuido !== '';
        },
        assigneesDisplayLabel() {
            const e = this.editedItem;
            const none = this.$t('ticket.usuario.none') || '-';
            if (!e) return none;
            const line = ticketAssigneesDisplayLabel(e);
            return line || none;
        },
        showTitulo() {
            return !this.useDetailFieldsConfig ? true : this.visibleDetailFields.includes('titulo');
        },
        /** From tipos_atendimento.usa_* ; true when tipo missing or not in list (same as create forms). */
        tipoAtendimentoUsaVeiculo() {
            const id = this.editedItem && this.editedItem.id_tipo_atendimento;
            if (id == null || id === '') return true;
            const t = (this.tiposAtendimento || []).find(x => Number(x.id_tipo_atendimento) === Number(id));
            if (!t) return true;
            return t.usa_veiculo === 1 || t.usa_veiculo === true || t.usa_veiculo === '1';
        },
        /** Equipment/kits column: same rule as create dialog (usa_equipamento || usa_kit). */
        tipoAtendimentoUsaEquipamento() {
            const id = this.editedItem && this.editedItem.id_tipo_atendimento;
            if (id == null || id === '') return true;
            const t = (this.tiposAtendimento || []).find(x => Number(x.id_tipo_atendimento) === Number(id));
            if (!t) return true;
            const eq = t.usa_equipamento === 1 || t.usa_equipamento === true || t.usa_equipamento === '1';
            const kit = t.usa_kit === 1 || t.usa_kit === true || t.usa_kit === '1';
            return eq || kit;
        },
        showVehicleEffective() {
            const base = !this.useDetailFieldsConfig ? this.showVehicle : this.visibleDetailFields.includes('vehicle');
            return base && this.tipoAtendimentoUsaVeiculo;
        },
        showEquipmentEffective() {
            const base = !this.useDetailFieldsConfig ? this.showEquipment : this.visibleDetailFields.includes('equipment');
            return base && this.tipoAtendimentoUsaEquipamento;
        },
        /** Vehicle column in the editable equipamentos card */
        showVehicleEditColumn() {
            return this.showVehicle && this.tipoAtendimentoUsaVeiculo;
        },
        /** Equipment column in the editable equipamentos card */
        showEquipmentEditColumn() {
            return this.showEquipment && this.tipoAtendimentoUsaEquipamento;
        },
        showStatusEffective() {
            return !this.useDetailFieldsConfig ? this.showStatus : this.visibleDetailFields.includes('status');
        },
        showStatusAtribuidoEffective() {
            return !this.useDetailFieldsConfig ? this.showStatusAtribuido : this.visibleDetailFields.includes('status_atribuido');
        },
        showStatusDispatcherEffective() {
            return !this.useDetailFieldsConfig ? this.showStatusDispatcher : this.visibleDetailFields.includes('status_dispatcher');
        },
        /**
         * Previsão de conclusão para o v-datetime-picker, que só lê string no formato dd/MM/yyyy HH:mm:ss;
         * a data vem do banco (ISO ou "YYYY-MM-DD HH:mm:ss") ou do próprio picker (Date). Converte sempre para Date.
         */
        expectedFinalizationPickerDate() {
            const v = this.expectedFinalizationDate || (this.editedItem && this.editedItem.dt_prevista_fim);
            if (!v) return null;
            if (v instanceof Date) return isNaN(v.getTime()) ? null : v;
            const str = String(v).trim();
            const local = /^(\d{4})-(\d{2})-(\d{2})[ T](\d{2}):(\d{2})(?::(\d{2}))?$/.exec(str);
            if (local) {
                return new Date(+local[1], +local[2] - 1, +local[3], +local[4], +local[5], +(local[6] || 0));
            }
            const br = /^(\d{2})\/(\d{2})\/(\d{4}) (\d{2}):(\d{2})(?::(\d{2}))?$/.exec(str);
            if (br) {
                return new Date(+br[3], +br[2] - 1, +br[1], +br[4], +br[5], +(br[6] || 0));
            }
            const d = new Date(str);
            return isNaN(d.getTime()) ? null : d;
        },
        showScheduleEffective() {
            return !this.useDetailFieldsConfig ? this.showSchedule : this.visibleDetailFields.includes('schedule');
        },
        showCorpo() {
            return !this.useDetailFieldsConfig ? true : this.visibleDetailFields.includes('corpo');
        },
        showEnderecoEffective() {
            return !this.useDetailFieldsConfig ? true : this.visibleDetailFields.includes('endereco');
        },
        showChecklistEffective() {
            return !this.useDetailFieldsConfig ? this.showChecklist : this.visibleDetailFields.includes('checklist');
        },
        showSelectionTreeEditEffective() {
            if (this.readOnlyTicketDetail) return false;
            return !this.useDetailFieldsConfig ? this.showSelectionTreeEdit : this.visibleDetailFields.includes('selection_tree');
        },
        showStatusChangeEffective() {
            if (this.readOnlyTicketDetail) return false;
            return !this.useDetailFieldsConfig ? this.showStatusChange : this.visibleDetailFields.includes('status_change');
        },
        showStatusAtribuidoChangeEffective() {
            if (this.readOnlyTicketDetail) return false;
            if (this.lockTaskStatusDialog) return false;
            return !this.useDetailFieldsConfig ? this.showStatusAtribuidoChange : this.visibleDetailFields.includes('status_atribuido_change');
        },
        showStatusDispatcherChangeEffective() {
            if (this.readOnlyTicketDetail) return false;
            if (this.lockDispatcherStatusToReadonly) return false;
            return !this.useDetailFieldsConfig ? this.showStatusDispatcherChange : this.visibleDetailFields.includes('status_dispatcher_change');
        },
        taskStatusItems() {
            return (this.statusTasksSelect && this.statusTasksSelect.length) ? this.statusTasksSelect : (this.status || []);
        },
        /** Technician: all task statuses; dispatcher: only closing statuses for the dropdown */
        taskStatusSelectItems() {
            const base = this.taskStatusItems || [];
            if (!this.allowDispatcherCloseTask) return base;
            const closingIds = [4, 5, 7, 8];
            const filtered = base.filter((s) => closingIds.indexOf(Number(s.id_status_ticket)) !== -1);
            return filtered.length ? filtered : base;
        },
        /**
         * API stores teams in id_equipes (comma-separated); legacy UI uses id_equipe.
         * Drive v-select from id_equipe when set, otherwise first id in id_equipes so the label shows nome_equipe.
         */
        teamSelectValue() {
            const item = this.editedItem || {};
            const direct = item.id_equipe;
            if (direct != null && direct !== '') {
                const n = Number(direct);
                return Number.isNaN(n) ? direct : n;
            }
            const ids = item.id_equipes;
            if (ids == null || ids === '') return null;
            if (Array.isArray(ids)) {
                if (ids.length === 0) return null;
                const n = Number(ids[0]);
                return Number.isNaN(n) ? null : n;
            }
            if (typeof ids === 'number' && Number.isFinite(ids)) return ids;
            const s = String(ids).trim();
            if (s === '') return null;
            const first = s.split(',')[0].trim();
            const parsed = parseInt(first, 10);
            return Number.isNaN(parsed) ? null : parsed;
        },
        showTeamEffective() {
            if (this.readOnlyTicketDetail) return false;
            return !this.useDetailFieldsConfig ? this.showTeam : this.visibleDetailFields.includes('team');
        },
        showUserAssignmentEffective() {
            if (this.readOnlyTicketDetail) return false;
            return !this.useDetailFieldsConfig ? this.showUserAssignment : this.visibleDetailFields.includes('user_assignment');
        },
        showScheduleEditEffective() {
            if (this.readOnlyTicketDetail) return false;
            return !this.useDetailFieldsConfig ? this.showScheduleEdit : this.visibleDetailFields.includes('schedule_edit');
        },
        showExpectedFinalizationEditEffective() {
            if (this.readOnlyTicketDetail) return false;
            return !this.useDetailFieldsConfig ? this.showExpectedFinalizationEdit : this.visibleDetailFields.includes('expected_finalization_edit');
        },
        showVehicleEquipmentEditEffective() {
            if (this.readOnlyTicketDetail) return false;
            const configAllows = !this.useDetailFieldsConfig ? (this.showEquipment || this.showVehicle) : this.visibleDetailFields.includes('vehicle_equipment_edit');
            if (!configAllows) return false;
            const wantV = this.showVehicle && this.tipoAtendimentoUsaVeiculo;
            const wantE = this.showEquipment && this.tipoAtendimentoUsaEquipamento;
            return wantV || wantE;
        },
        showChecklistSectionEffective() {
            if (this.isAgenciaCandidatosTicket || this.isSuporteSistemaTicket) {
                return true;
            }
            return !this.useDetailFieldsConfig ? this.showChecklistSection : this.visibleDetailFields.includes('checklist_section');
        },
        /** Checklist obrigatório preenchido ou sem checklist / resposta já salva */
        checklistFulfilledForTasksCta() {
            const t = this.editedItem;
            if (!t || !t.id_ticket) return false;
            if (!t.id_checklist_equipamento) return true;
            if (this.checklistItem && this.checklistItem.id_resposta) return true;
            if (!this.checklistSelecionado || !this.checklistSelecionado.campos || !this.checklistSelecionado.campos.length) {
                return !!(this.checklistItem && (this.checklistItem.dt_realizacao || this.checklistItem.dt_alteracao_extenso || this.checklistItem.nome_equipamento));
            }
            const campos = this.checklistSelecionado.campos;
            for (let i = 0; i < campos.length; i++) {
                const c = campos[i];
                if (!c.obrigatorio) continue;
                const v = this.checklistValoresCampos[c.id_campo];
                if (v === undefined || v === null || v === '') return false;
                if (c.tipo === 'Checkbox' && v !== true && v !== 'true' && v !== 1 && v !== '1') return false;
            }
            return true;
        },
        showTasksTabStatusChangeCta() {
            return (
                !this.readOnlyTicketDetail &&
                this.showTasksTabStatusCta &&
                this.showStatusAtribuidoChangeEffective &&
                !this.isTicketClosed &&
                this.checklistFulfilledForTasksCta
            );
        },
        showClosingObservationsEffective() {
            return !this.useDetailFieldsConfig ? true : this.visibleDetailFields.includes('closing_observations');
        },
        showInternalObservationEffective() {
            return !this.hideInternalObservation;
        },
        /** True when the Information tab has any visible content in the right column (edit cards). When false, left column uses full width. */
        hasRightColumnContent() {
            return (this.showSelectionTreeEditEffective && this.editedItem && this.editedItem.id_ticket) ||
                this.showStatusChangeEffective ||
                this.showStatusAtribuidoChangeEffective ||
                this.showStatusDispatcherChangeEffective ||
                this.showTeamEffective ||
                this.showUserAssignmentEffective ||
                this.showScheduleEditEffective ||
                this.showExpectedFinalizationEditEffective ||
                this.showSlaManualEditEffective ||
                (this.showVehicleEquipmentEditEffective && !this.isTicketClosed);
        },
        /** SLA manual editável para quem pode alterar o ticket (não depende da config de campos do detalhe). */
        showSlaManualEditEffective() {
            return !this.readOnlyTicketDetail && !this.isViewerOnly && !!(this.editedItem && this.editedItem.id_ticket);
        },
        slaManualItems() {
            return SLA_MANUAL_FAIXAS.map((f) => ({ value: f, text: this.$t('ticket.sla.option', { n: parseInt(f, 10) }) }));
        },
        changeStatusDialogDtFimMinDateProps() {
            const dt = this.changeStatusDialogDtInicio;
            if (!dt) return {};
            let yyyyMmDd;
            if (dt instanceof Date) {
                yyyyMmDd = dt.getFullYear() + '-' + String(dt.getMonth() + 1).padStart(2, '0') + '-' + String(dt.getDate()).padStart(2, '0');
            } else if (typeof dt === 'string') {
                const d = new Date(dt);
                if (isNaN(d.getTime())) return {};
                yyyyMmDd = d.getFullYear() + '-' + String(d.getMonth() + 1).padStart(2, '0') + '-' + String(d.getDate()).padStart(2, '0');
            } else {
                return {};
            }
            return { min: yyyyMmDd };
        },
        lastTask() {
            if (!this.ticketTasks || this.ticketTasks.length === 0) return null;
            return this.ticketTasks[this.ticketTasks.length - 1];
        },
        /** Anexos that belong to the last task only (dt_inclusao >= last task dt_alteracao, with small buffer) */
        ticketAnexosLastTask() {
            if (!this.ticketAnexos || this.ticketAnexos.length === 0) return [];
            const last = this.lastTask;
            if (!last || !last.dt_alteracao) return [];
            const taskTime = new Date(last.dt_alteracao).getTime();
            const bufferMs = 15000; // 15s before task time (same action can insert anexos then task)
            return this.ticketAnexos.filter((a) => {
                if (!a.dt_inclusao) return false;
                const anexoTime = new Date(a.dt_inclusao).getTime();
                return anexoTime >= taskTime - bufferMs;
            });
        },
        changeStatusDialogFormValid() {
            if (this.changeStatusDialogIdStatus == null || this.changeStatusDialogIdStatus === '') return false;
            if (!this.changeStatusDialogDtInicio) return false;
            if (!this.changeStatusDialogDtFim) return false;
            const ext = this.changeStatusDialogObservacaoExterna;
            if (!ext || !String(ext).trim()) return false;
            if (this.changeStatusDialogDtInicio && this.changeStatusDialogDtFim) {
                const inicio = this.changeStatusDialogDtInicio instanceof Date ? this.changeStatusDialogDtInicio.getTime() : new Date(this.changeStatusDialogDtInicio).getTime();
                const fim = this.changeStatusDialogDtFim instanceof Date ? this.changeStatusDialogDtFim.getTime() : new Date(this.changeStatusDialogDtFim).getTime();
                if (fim < inicio) return false;
            }
            return true;
        },
        isViewerOnly() {
            return !!(this.editedItem && (this.editedItem.is_viewer_only === 1 || this.editedItem.is_viewer_only === true));
        },
        selectedVehicleId() {
            const pair = this.vehicleEquipmentPairs && this.vehicleEquipmentPairs[0];
            return pair ? pair.id_veiculo : null;
        },
        selectedEquipamentos() {
            const pair = this.vehicleEquipmentPairs && this.vehicleEquipmentPairs[0];
            return (pair && pair.equipamentos) ? [...pair.equipamentos] : [];
        },
        /** Same rules as TicketCreateEquipamentosFields.getEquipamentosForRow; includes kits when id_veiculo matches */
        equipamentosForDetailSelect() {
            const list = this.equipamentosFiltradosPorEmpresa || [];
            if (!list.length) return list;
            const filterOn = this.filterEquipamentosByVehicle && this.selectedVehicleId != null;
            if (!filterOn) return list;
            const idVeiculo = Number(this.selectedVehicleId);
            return list.filter(e => {
                if (e.tipo === 'kit') return true; // keep kits visible even when vehicle filter is active
                return Number(e.id_veiculo) === idVeiculo;
            });
        },
        /** Equipment/kit list with display text and type name for the detail view */
        equipamentosComTipo() {
            const list = this.editedItem && this.editedItem.equipamentos_associados;
            if (!list || !Array.isArray(list) || list.length === 0) return [];
            return list.map(e => {
                const isKit = e.tipo === 'kit' || e.tipo === 1 || e.id_tipo_kit != null;
                let displayText = '';
                if (isKit) {
                    displayText = '[KIT] ' + (e.nome_equipamento || e.cod_equipamento || '');
                } else {
                    const eqLabel = e.codigo_qrcode || e.nome_equipamento;
                    if (eqLabel) {
                        displayText = e.cod_equipamento ? `${eqLabel} (${e.cod_equipamento})` : eqLabel;
                    } else {
                        displayText = e.cod_equipamento || `ID: ${e.id_equipamento}`;
                    }
                }
                const typeName = e.nome_tipo_equipamento || e.nome_tipo_kit || null;
                return { id_equipamento: e.id_equipamento, displayText, typeName };
            });
        },
        /** Equipments that belong to kits linked to the ticket (secondary association). */
        equipamentosKitLinhas() {
            const list = this.editedItem && this.editedItem.equipamentos_do_kit;
            if (!list || !Array.isArray(list) || list.length === 0) return [];
            return list.map((r) => {
                const kitLabel = this.$t('ticket.detail.kitLineItemRef', {
                    kit: r.kit_cod || r.id_kit || '—'
                });
                const label = r.codigo_qrcode || r.nome_equipamento || r.nome_tipo_equipamento || r.cod_equipamento;
                let itemText = label
                    ? r.cod_equipamento && label !== r.cod_equipamento
                        ? `${label} (${r.cod_equipamento})`
                        : label
                    : r.cod_equipamento || (r.id_equipamento != null ? `ID: ${r.id_equipamento}` : (r.id_kit_equipamento != null ? `—` : ''));
                if (r.quantidade != null && r.quantidade > 1 && itemText) {
                    itemText = `${itemText} ×${r.quantidade}`;
                }
                return {
                    id_kit: r.id_kit,
                    id_kit_equipamento: r.id_kit_equipamento,
                    id_equipamento: r.id_equipamento,
                    kitLabel,
                    itemText
                };
            });
        },
        /** All checklist photos (field photos + additional) for gallery at end of checklist */
        checklistAllPhotosList() {
            const list = [];
            if (!this.checklistSelecionado || !this.checklistSelecionado.campos) return list;
            this.checklistSelecionado.campos.filter(c => c.tipo === 'Photo').forEach(campo => {
                const valor = this.checklistValoresCampos[campo.id_campo];
                if (valor) {
                    const src = this.getPhotoUrl(valor);
                    if (src) list.push({ src, label: campo.label || this.$t('equipmentChecklist.noPhoto') });
                }
            });
            (this.checklistItem.fotos_extra || []).forEach((path, idx) => {
                const src = this.getPhotoUrl(path);
                if (src) list.push({ src, label: this.$t('equipmentChecklist.additionalPhotos') + ' ' + (idx + 1) });
            });
            return list;
        }
    },
    methods: {
        rotuloStatus(item) { return rotuloCatalogo(this, item, 'status_ticket'); },
        rotuloFamilia(item) { return rotuloCatalogo(this, item, 'nome_familia_atendimento'); },
        rotuloSolicitacao(item) { return rotuloCatalogo(this, item, 'nome_tipo_solicitacao'); },
        rotuloAtendimento(item) { return rotuloCatalogo(this, item, 'nome_tipo_atendimento'); },
        rotuloServico(item) { return rotuloCatalogo(this, item, 'nome_servico'); },
        onVehicleInput(id_veiculo) {
            const equipamentos = this.selectedEquipamentos;
            if (id_veiculo == null || id_veiculo === '') {
                this.filterEquipamentosByVehicle = false;
            }
            this.$emit('update:vehicleEquipmentPairs', [{ id_veiculo: id_veiculo || null, equipamentos }]);
        },
        toggleFilterEquipamentosByVehicle() {
            this.filterEquipamentosByVehicle = !this.filterEquipamentosByVehicle;
        },
        onEquipamentosInput(equipamentos) {
            const id_veiculo = this.selectedVehicleId;
            this.$emit('update:vehicleEquipmentPairs', [{ id_veiculo, equipamentos: equipamentos || [] }]);
        },
        openEquipmentQRScanner() {
            this.equipmentQrScannerError = null;
            this.equipmentQrScannerDialog = true;
        },
        closeEquipmentQRScanner() {
            this.equipmentQrScannerDialog = false;
            this.equipmentQrScannerError = null;
            this.equipmentQrCamera = 'auto';
        },
        onEquipmentQRDecode(decodedString) {
            const list = this.equipamentosFiltradosPorEmpresa || [];
            let item = null;
            try {
                const qrData = JSON.parse(decodedString);
                if (qrData.id_equipamento != null) {
                    item = list.find(e => e.id_equipamento === qrData.id_equipamento || e.id_equipamento === parseInt(qrData.id_equipamento, 10));
                }
                if (!item && qrData.id_kit != null) {
                    item = list.find(e => e.id_equipamento === qrData.id_kit || e.id_equipamento === parseInt(qrData.id_kit, 10));
                }
                if (!item && qrData.cod_equipamento) {
                    item = list.find(e => e.cod_equipamento === qrData.cod_equipamento);
                }
                if (!item && qrData.cod_kit) {
                    item = list.find(e => e.cod_equipamento === qrData.cod_kit);
                }
                if (!item && qrData.codigo_qrcode) {
                    item = list.find(e => e.codigo_qrcode === qrData.codigo_qrcode);
                }
            } catch (_) {
                item = list.find(e => e.codigo_qrcode === decodedString || e.cod_equipamento === decodedString);
            }
            if (item) {
                const value = item.value != null ? item.value : item.id_equipamento;
                const current = this.selectedEquipamentos || [];
                if (!current.includes(value)) {
                    const id_veiculo = this.selectedVehicleId;
                    this.$emit('update:vehicleEquipmentPairs', [{ id_veiculo, equipamentos: [...current, value] }]);
                }
                this.closeEquipmentQRScanner();
                EventBus.$emit('show-alert', {
                    mensagem: this.$t('equipmentChecklist.equipmentSelected') || this.$t('equipment.qrScanner.success') || 'Equipamento adicionado.',
                    tipo: 'success'
                });
            } else {
                this.equipmentQrScannerError = this.$t('equipmentChecklist.equipmentNotFound') || this.$t('equipment.qrScanner.invalidJSON') || 'Equipamento não encontrado ou não disponível para este ticket.';
            }
        },
        onEquipmentQRInit(promise) {
            promise.then(() => {
                this.equipmentQrScannerError = null;
            }).catch((err) => {
                let msg = this.$t('equipment.qrScanner.cameraError') || 'Erro ao acessar a câmera.';
                if (err && err.name === 'NotAllowedError') msg = this.$t('equipment.qrScanner.cameraPermissionDenied') || 'Permissão de câmera negada.';
                else if (err && err.name === 'NotFoundError') msg = this.$t('equipment.qrScanner.cameraNotFound') || 'Câmera não encontrada.';
                this.equipmentQrScannerError = msg;
            });
        },
        getChecklistMediaTypeFromPath(path) {
            if (!path || typeof path !== 'string') return 'image';
            const p = path.toLowerCase();
            if (/\.(mp4|mov|avi|webm)$/.test(p)) return 'video';
            if (/\.zip$/.test(p)) return 'zip';
            if (/\.(csv|txt|xlsx|xls)$/.test(p)) return 'document';
            return 'image';
        },
        getSuporteSistemaAnexoType(anexo) {
            const path = [
                anexo && anexo.caminho_arquivo,
                anexo && anexo.nome_arquivo_armazenado,
                anexo && anexo.nome_arquivo_original,
            ].filter(Boolean).join(' ');
            return this.getChecklistMediaTypeFromPath(path);
        },
        getPhotoUrl(path) {
            if (!path) {
                return '';
            }
            // If path already starts with http or https, use it as is
            if (path.startsWith('http://') || path.startsWith('https://')) {
                return path;
            }
            // The API returns: "static/uploads/checklists_equipamentos/filename.jpg"
            // express.static serves from /static root, so we need to remove "static/" prefix
            // Result: "/uploads/checklists_equipamentos/filename.jpg"
            let cleanPath = path;
            if (cleanPath.startsWith('static/uploads/')) {
                cleanPath = cleanPath.substring(7); // Remove "static/" -> "uploads/..."
            } else if (cleanPath.startsWith('static/')) {
                cleanPath = cleanPath.substring(7); // Remove "static/" -> "..."
            }
            // Ensure leading slash
            if (!cleanPath.startsWith('/')) {
                cleanPath = '/' + cleanPath;
            }
            return `${ApiBaseUrl}${cleanPath}`;
        },
        openPhoto(url) {
            window.open(url, '_blank');
        },
        handleImageError(event) {
            // Replace broken image with placeholder text
            event.target.style.display = 'none';
            const placeholder = document.createElement('div');
            placeholder.textContent = this.$t('equipmentChecklist.imageLoadError');
            placeholder.className = 'text-center pa-4 grey lighten-2';
            event.target.parentNode.appendChild(placeholder);
        },
        async fetchPrecandidatosPorTicket(id_ticket) {
            this.precandidatosList = [];
            this.precTiposTurnosPrecandidatosList = [];
            this.precColunasExtras = [];
            this.precLinhasExtras = [];
            this.precDownloadExpirado = false;
            this.precDtLimiteDownload = null;
            if (!id_ticket) return;
            const e = this.editedItem;
            if (!e || String(e.tipo_ticket_extenso || '').trim() !== TICKET_TYPE_SOLICITACAO_CANDIDATOS_AGENCIA) {
                return;
            }
            const t = this.token || (typeof sessionStorage !== 'undefined' ? sessionStorage.getItem('api') : null);
            if (!t) return;
            this.loadingPrecandidatos = true;
            try {
                const res = await apiController.request(apiController.endpoints.getPrecandidatosbyticket, {
                    params: { id_ticket },
                    silent: true,
                });
                if (res.data && !res.data.erro && Array.isArray(res.data.dados)) {
                    this.precandidatosList = res.data.dados;
                }
                const tipos = res.data && Array.isArray(res.data.tipos_turnos_precandidatos) ? res.data.tipos_turnos_precandidatos : [];
                this.precTiposTurnosPrecandidatosList = tipos;
                this.precColunasExtras = res.data && Array.isArray(res.data.colunas_extras) ? res.data.colunas_extras : [];
                this.precLinhasExtras = res.data && Array.isArray(res.data.linhas_extras) ? res.data.linhas_extras : [];
                this.precDownloadExpirado = !!(res.data && res.data.download_expirado);
                this.precDtLimiteDownload = (res.data && res.data.dt_limite_download_extenso) || null;
                if (!tipos.length) {
                    this.fetchPrecTurnos(t);
                } else {
                    this.precTurnosList = [];
                }
                if (res.data && Array.isArray(res.data.situacoes) && res.data.situacoes.length > 0) {
                    this.applyPrecSituacoesListFromApi(res.data.situacoes);
                } else {
                    this.fetchPrecSituacoes(t);
                }
            } catch (_) {
                this.precandidatosList = [];
            } finally {
                this.loadingPrecandidatos = false;
            }
        },
        /** Sem situacao gravada o select fica vazio: nunca assume a primeira da lista. */
        precValidacaoValue(item) {
            const n = Number(item && item.validacao);
            if (!Number.isFinite(n) || n <= 0) {
                return null;
            }
            const list = this.precSituacoesList || [];
            if (!list.length) {
                return n;
            }
            const ids = new Set(list.map((s) => Number(s.id_situacao_validacao)));
            return ids.has(n) ? n : null;
        },
        /** Same strings as CRUDCandidatos.labelForValidoCode (candidatos / valido por turno). */
        precSituacaoLabelForValido(v) {
            const n = Number(v);
            if (!Number.isFinite(n)) {
                return this.$t('addressValidation.statusDoesNotAttend');
            }
            switch (n) {
                case 1:
                    return this.$t('addressValidation.statusAttends');
                case 2:
                    return this.$t('addressValidation.statusAttendsNoSeat');
                case 3:
                    return this.$t('addressValidation.statusAttendsNewStop');
                case 4:
                    return this.$t('addressValidation.statusOutsideUnit');
                default:
                    return this.$t('addressValidation.statusDoesNotAttend');
            }
        },
        turnoPrecValSlot(t) {
            return 'item.' + turnoPrecandidatoValSlotName(t.id_turno);
        },
        tipoPrecValSlot(tt) {
            return 'item.' + turnoTipoPrecValSlotName(tt.id_tipo_turno_precandidato);
        },
        precTurnoValidationHeader(t) {
            if (!t) return '';
            const nome = String(
                (t.descricao_turno && t.descricao_turno.trim()) || (t.cod_turno && t.cod_turno.trim()) || `#${t.id_turno}`
            ).trim();
            const h = getTurnoHorarioSemanal(t);
            if (h.inicio && h.fim) {
                return this.$t('ticket.detail.precandidatos.turnoVal.headerWithHours', {
                    nome,
                    ini: h.inicio,
                    fim: h.fim,
                });
            }
            return this.$t('ticket.detail.precandidatos.turnoVal.headerNoHours', { nome });
        },
        precTurnoColumnTooltip(t) {
            if (!t) return '';
            const nome = String(
                (t.descricao_turno && t.descricao_turno.trim()) || (t.cod_turno && t.cod_turno.trim()) || `#${t.id_turno}`
            ).trim();
            const h = getTurnoHorarioSemanal(t);
            if (!h.inicio || !h.fim) {
                return this.$t('ticket.detail.precandidatos.turnoVal.titleNoSchedule', { nome });
            }
            return this.$t('ticket.detail.precandidatos.turnoVal.titleMatch', {
                nome,
                ini: h.inicio,
                fim: h.fim,
            });
        },
        precValidacaoPorTurnoValue(item, t) {
            const map = precParseValidacaoPorTurnoMap(item && item.validacao_por_turno);
            const v = map[String(t && t.id_turno)];
            const n = v != null ? Number(v) : NaN;
            if (Number.isFinite(n) && n >= 0 && n <= 4) {
                return n;
            }
            return 0;
        },
        precTipoTurnoTextValue(item, idTipo) {
            const m = item && item.valores_tipos_turno;
            if (!m) {
                return '';
            }
            const v = m[String(idTipo)];
            return v != null ? String(v) : '';
        },
        onPrecTipoTurnoTextBlur(item, tt, ev) {
            const raw = ev && ev.target != null ? ev.target.value : '';
            this.onPrecTipoTurnoTextChange(item, tt.id_tipo_turno_precandidato, raw);
        },
        onPrecTipoTurnoTextChange(item, idTipo, val) {
            if (this.precCandidatosWriteDisabled) return;
            const next = val != null && val !== undefined ? String(val) : '';
            if (this.precTipoTurnoTextValue(item, idTipo) === next) return;
            this.savePrecandidatoFlags(item, {
                id_tipo_turno_precandidato: idTipo,
                valor_tipo_turno: next,
            });
        },
        /** Célula da atualização parcial: grava em pre_candidatos_valores_extras (vai para a grade ao finalizar o ticket). */
        onPrecExtraTextBlur(item, c, ev) {
            if (this.precCandidatosWriteDisabled) return;
            const next = ev && ev.target != null ? String(ev.target.value).trim() : '';
            const atual = item['extra_' + c.id_coluna];
            if ((atual == null ? '' : String(atual)) === next) return;
            this.savePrecandidatoFlags(item, { id_coluna_extra: c.id_coluna, valor_extra: next });
        },
        onPrecValidacaoPorTurnoChange(item, t, val) {
            if (this.precCandidatosWriteDisabled) return;
            let next = 0;
            if (val !== undefined && val !== null && val !== '') {
                const n = Number(val);
                next = Number.isFinite(n) && n >= 0 && n <= 4 ? n : 0;
            }
            if (this.precValidacaoPorTurnoValue(item, t) === next) return;
            this.savePrecandidatoFlags(item, {
                id_turno_validacao: t.id_turno,
                validacao_turno: next,
            });
        },
        fetchPrecTurnos(token) {
            const t = token || this.token || (typeof sessionStorage !== 'undefined' ? sessionStorage.getItem('api') : null);
            if (!t) return;
            apiController.request(apiController.endpoints.getallTurnos, {
                silent: true,
                onSuccess: (res) => {
                    const d = res.data;
                    if (d && d.erro) return;
                    if (Array.isArray(d)) {
                        this.precTurnosList = d;
                    }
                },
            }).catch(() => {});
        },
        applyPrecSituacoesListFromApi(list) {
            const d = list;
            if (!Array.isArray(d) || !d.length) {
                return;
            }
            this.precSituacoesList = d.slice().sort((a, b) => {
                const pa = Number(a.prioridade);
                const pb = Number(b.prioridade);
                if (Number.isFinite(pa) && Number.isFinite(pb) && pa !== pb) {
                    return pb - pa;
                }
                return String(a.nome || '').localeCompare(String(b.nome || ''), undefined, { sensitivity: 'base' });
            });
        },
        fetchPrecSituacoes(token) {
            const t = token || this.token || (typeof sessionStorage !== 'undefined' ? sessionStorage.getItem('api') : null);
            if (!t) return;
            apiController.request(apiController.endpoints.getallSituacoesvalidacao, {
                silent: true,
                onSuccess: (res) => {
                    const d = res.data;
                    if (d && d.erro) return;
                    if (!Array.isArray(d)) {
                        this.precSituacoesList = [];
                        return;
                    }
                    this.applyPrecSituacoesListFromApi(d);
                },
                onError: () => {
                    this.precSituacoesList = [];
                },
            });
        },
        onPrecValidacaoSelectChange(item, val) {
            if (this.precCandidatosWriteDisabled) return;
            const list = this.precSituacoesList || [];
            let next = this.precValidacaoValue(item);
            if (val !== undefined && val !== null && val !== '') {
                const n = Number(val);
                if (Number.isFinite(n) && n > 0) {
                    next = n;
                }
            } else if (list.length) {
                next = Number(list[0].id_situacao_validacao);
            }
            if (!Number.isFinite(Number(next)) || Number(next) <= 0) return;
            if (Number(item && item.validacao) === Number(next)) return;
            this.savePrecandidatoFlags(item, { validacao: next });
        },
        async savePrecandidatoFlags(item, patch) {
            if (!this.editedItem || !this.editedItem.id_ticket) return;
            const t = this.token || (typeof sessionStorage !== 'undefined' ? sessionStorage.getItem('api') : null);
            if (!t) return;
            this.precSavingId = item.id_pre_candidato;
            try {
                const res = await apiController.request(apiController.endpoints.updatePrecandidato, {
                    data: Object.assign(
                        {
                            id_pre_candidato: item.id_pre_candidato,
                            id_ticket: this.editedItem.id_ticket,
                        },
                        patch
                    ),
                    silent: true,
                });
                {
                    if (res.data && res.data.erro) {
                        EventBus.$emit('show-alert', { tipo: 'error', mensagem: (res.data.mensagem || '').toString() });
                        return;
                    }
                    if (patch.validacao !== undefined) {
                        item.validacao = Number(patch.validacao);
                    }
                    if (patch.id_coluna_extra !== undefined) {
                        const linha = (this.precLinhasExtras || []).find(
                            (l) => String(l.id_pre_candidato) === String(item.id_pre_candidato)
                        );
                        if (linha) {
                            if (!linha.valores) this.$set(linha, 'valores', {});
                            this.$set(linha.valores, String(patch.id_coluna_extra), patch.valor_extra === '' ? null : patch.valor_extra);
                        }
                    }
                    if (patch.id_tipo_turno_precandidato !== undefined && patch.valor_tipo_turno !== undefined) {
                        const idT = String(patch.id_tipo_turno_precandidato);
                        if (!item.valores_tipos_turno) {
                            this.$set(item, 'valores_tipos_turno', {});
                        }
                        if (patch.valor_tipo_turno === '') {
                            this.$delete(item.valores_tipos_turno, idT);
                        } else {
                            this.$set(item.valores_tipos_turno, idT, patch.valor_tipo_turno);
                        }
                    }
                    if (patch.id_turno_validacao !== undefined && patch.validacao_turno !== undefined) {
                        const m = precParseValidacaoPorTurnoMap(item.validacao_por_turno);
                        m[String(patch.id_turno_validacao)] = Number(patch.validacao_turno);
                        (this.precTurnosList || []).forEach((t) => {
                            const k = String(t.id_turno);
                            if (m[k] === undefined || m[k] === null) {
                                m[k] = 0;
                            }
                        });
                        item.validacao_por_turno = m;
                    }
                }
            } catch (e) {
                const msg = e.response && e.response.data && e.response.data.mensagem;
                EventBus.$emit('show-alert', {
                    tipo: 'error',
                    mensagem: msg ? String(msg) : (this.$t('ticket.alerts.error.generic') || 'Erro'),
                });
            } finally {
                this.precSavingId = null;
            }
        },
        async downloadPrecandidatosCsv() {
            const id = this.editedItem && this.editedItem.id_ticket;
            if (!id || this.precDownloadExpirado) return;
            const t = this.token || (typeof sessionStorage !== 'undefined' ? sessionStorage.getItem('api') : null);
            if (!t) return;
            this.precCsvDownloading = true;
            try {
                const res = await apiController.request(apiController.endpoints.getPrecandidatoscsvticket, {
                    params: { id_ticket: id },
                    config: { responseType: 'blob' },
                    silent: true,
                });
                const blob = new Blob([res.data], { type: 'text/csv;charset=utf-8' });
                const url = window.URL.createObjectURL(blob);
                const a = document.createElement('a');
                a.href = url;
                a.download = 'pre_candidatos_ticket_' + id + '.csv';
                document.body.appendChild(a);
                a.click();
                a.remove();
                window.URL.revokeObjectURL(url);
            } catch (e) {
                // responseType blob: a mensagem de erro da API (ex.: prazo encerrado) vem como Blob.
                let msg = null;
                const data = e && e.response && e.response.data;
                if (data && typeof data.text === 'function') {
                    try { msg = JSON.parse(await data.text()).mensagem || null; } catch (_) { msg = null; }
                }
                EventBus.$emit('show-alert', { tipo: 'error', mensagem: msg || this.$t('ticket.detail.precandidatos.downloadError') });
            } finally {
                this.precCsvDownloading = false;
            }
        },
        /**
         * Mesmas colunas do CSV normal (getprecandidatoscsvticket), só com as linhas atualizadas parcialmente.
         * Coluna extra com o mesmo nome de um tipo existente sobrepõe o valor atual (como no "Finalizar alteração");
         * as demais entram no fim, como novos tipos.
         */
        downloadPrecExtrasCsv() {
            if (this.precDownloadExpirado) return;
            const esc = (v) => {
                const str = v === null || v === undefined ? '' : String(v);
                return /[";\r\n]/.test(str) ? '"' + str.replace(/"/g, '""') + '"' : str;
            };
            const normNome = (n) => String(n == null ? '' : n)
                .replace(/^\ufeff/, '')
                .normalize('NFD')
                .replace(/[̀-ͯ]/g, '')
                .trim()
                .toLowerCase()
                .replace(/\s+/g, ' ');
            const fixos = ['id_pre_candidato', 'linha_arquivo', 'nome_completo', 'rua', 'numero', 'bairro', 'cidade', 'cep', 'turno_desejado'];
            const extrasPorNome = {};
            (this.precColunasExtras || []).forEach((c) => { extrasPorNome[normNome(c.nome)] = c; });
            const colunas = (this.precTiposTurnosPrecandidatosList || []).map((t) => ({
                nome: t.nome,
                idTipo: String(t.id_tipo_turno_precandidato),
                extra: extrasPorNome[normNome(t.nome)] || null,
            }));
            const usados = {};
            colunas.forEach((c) => { if (c.extra) usados[c.extra.id_coluna] = true; });
            (this.precColunasExtras || []).forEach((c) => {
                if (!usados[c.id_coluna]) colunas.push({ nome: c.nome, idTipo: null, extra: c });
            });
            const basePorId = {};
            (this.precandidatosList || []).forEach((r) => { basePorId[String(r.id_pre_candidato)] = r; });

            const lines = [fixos.concat(colunas.map((c) => esc(c.nome))).join(';')];
            (this.precLinhasExtras || []).forEach((l) => {
                const base = basePorId[String(l.id_pre_candidato)] || l;
                const atuais = base.valores_tipos_turno || {};
                const novos = l.valores || {};
                const cells = fixos.map((f) => esc(base[f]));
                colunas.forEach((c) => {
                    const novo = c.extra ? novos[String(c.extra.id_coluna)] : null;
                    const temNovo = novo !== null && novo !== undefined && String(novo) !== '';
                    cells.push(esc(temNovo ? novo : (c.idTipo ? atuais[c.idTipo] : null)));
                });
                lines.push(cells.join(';'));
            });
            const blob = new Blob(['\ufeff' + lines.join('\r\n')], { type: 'text/csv;charset=utf-8' });
            const url = window.URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = 'pre_candidatos_colunas_atualizadas_ticket_' + (this.editedItem && this.editedItem.id_ticket) + '.csv';
            document.body.appendChild(a);
            a.click();
            a.remove();
            window.URL.revokeObjectURL(url);
        },
        triggerPrecandidatosCsvUpload() {
            if (this.precCandidatosWriteDisabled) return;
            const a = this.$refs.precandidatosCsvInput;
            const b = this.$refs.precandidatosCsvInputAgencia;
            const el = a && typeof a.click === 'function' ? a : b;
            if (el && typeof el.click === 'function') {
                el.click();
            }
        },
        async onPrecandidatosCsvSelected(ev) {
            const input = ev && ev.target;
            if (this.precCandidatosWriteDisabled) {
                if (input) input.value = '';
                return;
            }
            const f = input && input.files && input.files[0];
            if (!f || !this.editedItem || !this.editedItem.id_ticket) {
                if (input) input.value = '';
                return;
            }
            const t = this.token || (typeof sessionStorage !== 'undefined' ? sessionStorage.getItem('api') : null);
            if (!t) {
                if (input) input.value = '';
                return;
            }
            this.precCsvPendingFile = f;
            const fd = new FormData();
            fd.append('file', f, f.name || 'pre_candidatos.csv');
            fd.append('id_ticket', String(this.editedItem.id_ticket));
            fd.append('dry_run', '1');
            this.precCsvUploading = true;
            await this.$nextTick();
            try {
                const res = await apiController.request(apiController.endpoints.importprecandidatoscsvticket, {
                    data: fd,
                    silent: true,
                });
                if (res.data && res.data.erro) {
                    EventBus.$emit('show-alert', { tipo: 'error', mensagem: (res.data.mensagem || '').toString() });
                    this.clearPrecandidatosCsvPreview();
                    return;
                }
                const dyn = Array.isArray(res.data && res.data.dynamicTurnoColumns)
                    ? res.data.dynamicTurnoColumns.map((c) => String((c && c.nome) || '').trim()).filter(Boolean)
                    : [];
                this.precCsvPreviewTipos = dyn;
                const rawRows = Array.isArray(res.data && res.data.rows) ? res.data.rows : [];
                this.precCsvPreviewRows = rawRows.map((r) => {
                    const flat = Object.assign({}, r);
                    const tiposMap = (r && r.tipos_turno) || {};
                    for (let i = 0; i < dyn.length; i++) {
                        const nome = dyn[i];
                        flat['precCsvTipo_' + i] = tiposMap[nome] != null ? tiposMap[nome] : '';
                    }
                    return flat;
                });
                this.precCsvResultDialog = true;
            } catch (e) {
                const msg = e.response && e.response.data && e.response.data.mensagem;
                EventBus.$emit('show-alert', {
                    tipo: 'error',
                    mensagem: msg ? String(msg) : this.$t('ticket.detail.precandidatos.uploadError'),
                });
                this.clearPrecandidatosCsvPreview();
            } finally {
                this.precCsvUploading = false;
                if (input) input.value = '';
            }
        },
        clearPrecandidatosCsvPreview() {
            this.precCsvResultDialog = false;
            this.precCsvPendingFile = null;
            this.precCsvPreviewRows = [];
            this.precCsvPreviewTipos = [];
            this.precCsvSaving = false;
        },
        cancelPrecandidatosCsvPreview() {
            if (this.precCsvSaving) return;
            this.clearPrecandidatosCsvPreview();
        },
        async confirmPrecandidatosCsvPreview() {
            const f = this.precCsvPendingFile;
            if (!f || !this.editedItem || !this.editedItem.id_ticket || this.precCsvSaving) {
                return;
            }
            const t = this.token || (typeof sessionStorage !== 'undefined' ? sessionStorage.getItem('api') : null);
            if (!t) return;
            const fd = new FormData();
            fd.append('file', f, f.name || 'pre_candidatos.csv');
            fd.append('id_ticket', String(this.editedItem.id_ticket));
            this.precCsvSaving = true;
            try {
                const res = await apiController.request(apiController.endpoints.importprecandidatoscsvticket, {
                    data: fd,
                    silent: true,
                });
                if (res.data && res.data.erro) {
                    EventBus.$emit('show-alert', { tipo: 'error', mensagem: (res.data.mensagem || '').toString() });
                    return;
                }
                const n = res.data && res.data.atualizados != null ? Number(res.data.atualizados) : 0;
                const fails = res.data && Array.isArray(res.data.falhas) ? res.data.falhas.length : 0;
                let msg = this.$t('ticket.detail.precandidatos.uploadSuccess', { n: n });
                if (fails > 0) {
                    msg += ' ' + this.$t('ticket.detail.precandidatos.uploadPartialFailures', { n: fails });
                }
                EventBus.$emit('show-alert', { tipo: 'success', mensagem: msg });
                this.clearPrecandidatosCsvPreview();
                this.fetchPrecandidatosPorTicket(this.editedItem.id_ticket);
                this.$emit('updated');
            } catch (e) {
                const msg = e.response && e.response.data && e.response.data.mensagem;
                EventBus.$emit('show-alert', {
                    tipo: 'error',
                    mensagem: msg ? String(msg) : this.$t('ticket.detail.precandidatos.uploadError'),
                });
            } finally {
                this.precCsvSaving = false;
            }
        },
        async fetchTicketTasks(id_ticket) {
            if (!id_ticket) return;
            const t = this.token || (typeof sessionStorage !== 'undefined' ? sessionStorage.getItem('api') : null);
            if (!t) {
                this.ticketTasks = [];
                return;
            }
            this.loadingTasks = true;
            try {
                const res = await apiController.request(apiController.endpoints.getTickettasks, {
                    params: { id_ticket },
                    silent: true,
                });
                if (res.data && !res.data.erro && Array.isArray(res.data.dados)) {
                    this.ticketTasks = res.data.dados;
                } else {
                    this.ticketTasks = [];
                }
            } catch (_) {
                this.ticketTasks = [];
            } finally {
                this.loadingTasks = false;
            }
        },
        /** Tasks tab: show all ticket assignees (junction), not only ticket_tasks.id_usuario_atribuido. */
        taskTabTechnicianLine(task) {
            const fromTicket = ticketAssigneesDisplayLabel(this.editedItem);
            if (fromTicket) return fromTicket;
            if (task && task.nome_usuario_atribuido) return task.nome_usuario_atribuido;
            return this.$t('ticket.usuario.none') || '-';
        },
        fetchTaskStatuses() {
            const t = this.token || (typeof sessionStorage !== 'undefined' ? sessionStorage.getItem('api') : null);
            if (!t) {
                this.statusTasksSelect = [];
                return;
            }
            apiController.request(apiController.endpoints.getallStatus, {
                query: this.allowDispatcherCloseTask ? {} : { screen: 'colaborador' },
                silent: true,
                onSuccess: (res) => {
                    const raw = Array.isArray(res.data) ? res.data : [];
                    this.statusTasksSelect = filterTicketStatusesByPerfil(raw, 'select');
                },
                onError: () => { this.statusTasksSelect = []; },
            });
        },
        taskRowCanEditStatus(task) {
            if (this.readOnlyTicketDetail) return false;
            if (this.isTicketClosed || !task) return false;
            if (this.allowDispatcherCloseTask) return true;
            if (!this.canEditInlineTaskStatus) return false;
            if (this.currentUserId == null || this.currentUserId === '') return false;
            const me = Number(this.currentUserId);
            if (!Number.isFinite(me)) return false;
            const assignees = Array.isArray(this.editedItem && this.editedItem.id_usuarios_atribuidos)
                ? this.editedItem.id_usuarios_atribuidos
                : [];
            if (assignees.some((id) => Number(id) === me)) return true;
            const rowUser = task.id_usuario_atribuido != null ? Number(task.id_usuario_atribuido) : null;
            if (rowUser == null || !Number.isFinite(rowUser)) return true;
            return rowUser === me;
        },
        onInlineTaskStatusChange(task, newVal) {
            const old = task.id_status_atribuido != null ? Number(task.id_status_atribuido) : null;
            const nv = newVal != null && newVal !== '' ? Number(newVal) : null;
            if (nv === null || !Number.isFinite(nv) || old === nv || !this.editedItem.id_ticket) return;
            this.saveInlineTaskStatus(task, nv);
        },
        async saveInlineTaskStatus(task, id_status_atribuido) {
            const t = this.token || (typeof sessionStorage !== 'undefined' ? sessionStorage.getItem('api') : null);
            if (!t || !this.editedItem.id_ticket) return;
            this.savingTaskSuffix = task.suffix;
            try {
                const res = await apiController.request(apiController.endpoints.updateTickettaskstatus, {
                    data: { id_ticket: this.editedItem.id_ticket, suffix: task.suffix, id_status_atribuido },
                    silent: true,
                });
                if (res.data && res.data.erro) {
                    EventBus.$emit('show-alert', { tipo: 'error', mensagem: res.data.mensagem || 'Erro ao atualizar status da tarefa.' });
                    return;
                }
                const label = (this.taskStatusSelectItems || []).find((s) => Number(s.id_status_ticket) === Number(id_status_atribuido));
                if (label) task.task_status_extenso = label.status_ticket;
                this.fetchTicketTasks(this.editedItem.id_ticket);
                this.$emit('updated');
            } catch (_) {
                EventBus.$emit('show-alert', { tipo: 'error', mensagem: this.$t('ticket.alerts.error.status') || 'Erro ao atualizar.' });
            } finally {
                this.savingTaskSuffix = null;
            }
        },
        async fetchTicketAnexos(id_ticket) {
            if (!id_ticket) return;
            const t = this.token || (typeof sessionStorage !== 'undefined' ? sessionStorage.getItem('api') : null);
            if (!t) {
                this.ticketAnexos = [];
                return;
            }
            this.loadingAnexos = true;
            try {
                const res = await apiController.request(apiController.endpoints.getTicketanexos, {
                    params: { id_ticket },
                    silent: true,
                });
                if (res.data && !res.data.erro && Array.isArray(res.data.dados)) {
                    this.ticketAnexos = res.data.dados;
                } else {
                    this.ticketAnexos = [];
                }
            } catch (_) {
                this.ticketAnexos = [];
            } finally {
                this.loadingAnexos = false;
            }
        },
        async fetchSuporteSistemaAnexos(id_ticket) {
            if (!id_ticket) {
                this.suporteSistemaAnexos = [];
                return;
            }
            this.loadingSuporteAnexos = true;
            try {
                const res = await apiController.request(apiController.endpoints.getTicketsuportesistemaanexos, {
                    params: { id_ticket },
                    silent: true,
                });
                if (res.data && !res.data.erro && Array.isArray(res.data.dados)) {
                    this.suporteSistemaAnexos = res.data.dados;
                } else {
                    this.suporteSistemaAnexos = [];
                }
            } catch (_) {
                this.suporteSistemaAnexos = [];
            } finally {
                this.loadingSuporteAnexos = false;
            }
        },
        openAssignUserDialog() {
            const e = this.editedItem || {};
            let ids = [];
            if (Array.isArray(e.id_usuarios_atribuidos) && e.id_usuarios_atribuidos.length > 0) {
                ids = e.id_usuarios_atribuidos.map((id) => Number(id)).filter((n) => Number.isFinite(n));
            } else if (e.id_usuario_atribuido != null && e.id_usuario_atribuido !== '') {
                const n = Number(e.id_usuario_atribuido);
                if (Number.isFinite(n)) ids = [n];
            }
            this.assignDialogUserIds = ids;
            this.assignDialogDescricao = '';
            this.assignDialogDtPrevistaFim = null;
            this.assignDialogAnexos = [];
            this.assignUserDialog = true;
        },
        onAssignUserAnexosSelected(e) {
            const files = e.target.files ? Array.from(e.target.files) : [];
            files.forEach((f) => {
                const isZip = f.type === 'application/zip' || f.type === 'application/x-zip-compressed' || (f.name && /\.zip$/i.test(f.name));
                this.assignDialogAnexos.push({ file: f, mediaType: isZip ? 'zip' : 'image' });
            });
            e.target.value = '';
        },
        removeAssignUserAnexo(idx) {
            this.assignDialogAnexos.splice(idx, 1);
        },
        onChangeStatusAnexosSelected(e) {
            const files = e.target.files ? Array.from(e.target.files) : [];
            files.forEach((f) => {
                const isZip = f.type === 'application/zip' || f.type === 'application/x-zip-compressed' || (f.name && /\.zip$/i.test(f.name));
                this.changeStatusDialogAnexos.push({ file: f, mediaType: isZip ? 'zip' : 'image' });
            });
            e.target.value = '';
        },
        removeChangeStatusAnexo(idx) {
            this.changeStatusDialogAnexos.splice(idx, 1);
        },
        uploadTicketAnexos(anexosList) {
            if (!anexosList || anexosList.length === 0) return Promise.resolve([]);
            const timestamp = Date.now();
            const uploadOne = (item, idx) => {
                return new Promise((resolve, reject) => {
                    const reader = new FileReader();
                    reader.onload = (ev) => {
                        const base64Data = ev.target.result;
                        const baseName = (item.file.name || 'file').replace(/\s/g, '_').replace(/\.[^.]+$/, '') || 'file';
                        const filename = `ticket_anexo_${timestamp}_${idx}_${baseName}`;
                        const uploadData = { file: base64Data, filename, folder: 'ticket_anexos' };
                        apiController.request(apiController.endpoints.uploadChecklistmedia, {
                            data: uploadData,
                            silent: true,
                            onSuccess: (res) => {
                                if (res.data && !res.data.error && res.data.path) resolve(res.data.path);
                                else reject(new Error(res.data?.message || 'Upload failed'));
                            },
                            onError: reject,
                        }).catch(() => {});
                    };
                    reader.onerror = () => reject(new Error('Failed to read file'));
                    reader.readAsDataURL(item.file);
                });
            };
            return Promise.all(anexosList.map((item, idx) => uploadOne(item, idx)));
        },
        formatDateToMySQL(dateValue) {
            if (!dateValue) return null;
            if (typeof dateValue === 'string' && /^\d{4}-\d{2}-\d{2} \d{2}:\d{2}:\d{2}$/.test(dateValue)) {
                return dateValue;
            }
            const date = dateValue instanceof Date ? dateValue : new Date(dateValue);
            if (isNaN(date.getTime())) return null;
            const year = date.getFullYear();
            const month = String(date.getMonth() + 1).padStart(2, '0');
            const day = String(date.getDate()).padStart(2, '0');
            const hours = String(date.getHours()).padStart(2, '0');
            const minutes = String(date.getMinutes()).padStart(2, '0');
            const seconds = String(date.getSeconds()).padStart(2, '0');
            return `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`;
        },
        confirmAssignUser() {
            if (!this.assignDialogUserIds || this.assignDialogUserIds.length === 0) return;
            const doEmit = (anexosPaths) => {
                const ids = [...this.assignDialogUserIds].map((id) => Number(id)).filter((n) => Number.isFinite(n));
                const names = ids.map((id) => {
                    const u = (this.usuariosPorEquipe || []).find((x) => Number(x.id_usuario) === id);
                    return u ? u.nome_usuario : null;
                }).filter(Boolean);
                const nome_usuario_atribuido = names.length ? names.join(', ') : null;
                this.$emit('update:user', {
                    id_usuarios_atribuidos: ids,
                    id_usuario_atribuido: ids.length ? ids[0] : null,
                    nome_usuario_atribuido,
                    descricao_tarefa: this.assignDialogDescricao && this.assignDialogDescricao.trim() ? this.assignDialogDescricao.trim() : null,
                    dt_prevista_fim: this.assignDialogDtPrevistaFim != null
                        ? this.formatDateToMySQL(this.assignDialogDtPrevistaFim)
                        : null,
                    anexos: (anexosPaths || []).map((path) => ({ path, tipo_alteracao: 'atribuicao' }))
                });
                this.assignUserDialog = false;
                this.assignDialogAnexos = [];
            };
            if (this.assignDialogAnexos.length === 0) {
                doEmit([]);
                return;
            }
            this.uploadingAssignAnexos = true;
            this.uploadTicketAnexos(this.assignDialogAnexos)
                .then((paths) => { doEmit(paths); })
                .catch((err) => {
                    EventBus.$emit('show-alert', { tipo: 'error', mensagem: this.$t('ticket.tasks.uploadError') || 'Erro ao enviar anexos.' });
                    console.error(err);
                })
                .finally(() => { this.uploadingAssignAnexos = false; });
        },
        applyInitialDetailTab() {
            if (this.initialDetailTab !== 'tasks' || !this.editedItem || !this.editedItem.id_ticket) return;
            this.$nextTick(() => {
                this.detailActiveTab = 'tab-tasks';
            });
        },
        openChangeStatusAtribuidoDialog() {
            this.changeStatusDialogIdStatus = this.editedItem.id_status_atribuido != null ? this.editedItem.id_status_atribuido : null;
            const assist = this.assistStatusDtInicio != null && this.assistStatusDtInicio !== '';
            if (assist) {
                const d = this.assistStatusDtInicio instanceof Date
                    ? this.assistStatusDtInicio
                    : new Date(this.assistStatusDtInicio);
                this.changeStatusDialogDtInicio = isNaN(d.getTime()) ? null : d;
                this.changeStatusDialogDtFim = new Date();
            } else {
                this.changeStatusDialogDtInicio = null;
                this.changeStatusDialogDtFim = null;
            }
            this.changeStatusDialogObservacaoExterna = '';
            this.changeStatusDialogObservacaoInterna = '';
            this.changeStatusDialogAnexos = [];
            this.changeStatusAtribuidoDialog = true;
        },
        confirmChangeStatusAtribuido() {
            if (this.changeStatusDialogIdStatus == null) return;
            if (this.changeStatusDialogDtInicio && this.changeStatusDialogDtFim) {
                const inicio = this.changeStatusDialogDtInicio instanceof Date ? this.changeStatusDialogDtInicio.getTime() : new Date(this.changeStatusDialogDtInicio).getTime();
                const fim = this.changeStatusDialogDtFim instanceof Date ? this.changeStatusDialogDtFim.getTime() : new Date(this.changeStatusDialogDtFim).getTime();
                if (fim < inicio) {
                    EventBus.$emit('show-alert', { tipo: 'error', mensagem: this.$t('ticket.tasks.finalDateNotBeforeInitial') || 'A data/hora final não pode ser anterior à inicial.' });
                    return;
                }
            }
            const doEmit = (anexosPaths) => {
                this.$emit('update:statusAtribuido', {
                    id_status_atribuido: this.changeStatusDialogIdStatus,
                    dt_inicio: this.changeStatusDialogDtInicio || null,
                    dt_fim: this.changeStatusDialogDtFim || null,
                    observacao_externa: this.changeStatusDialogObservacaoExterna && this.changeStatusDialogObservacaoExterna.trim() ? this.changeStatusDialogObservacaoExterna.trim() : null,
                    observacao_interna: this.changeStatusDialogObservacaoInterna && this.changeStatusDialogObservacaoInterna.trim() ? this.changeStatusDialogObservacaoInterna.trim() : null,
                    anexos: (anexosPaths || []).map((path) => ({ path, tipo_alteracao: 'status' }))
                });
                this.changeStatusAtribuidoDialog = false;
                this.changeStatusDialogAnexos = [];
            };
            if (this.changeStatusDialogAnexos.length === 0) {
                doEmit([]);
                return;
            }
            this.uploadingStatusAnexos = true;
            this.uploadTicketAnexos(this.changeStatusDialogAnexos)
                .then((paths) => { doEmit(paths); })
                .catch((err) => {
                    EventBus.$emit('show-alert', { tipo: 'error', mensagem: this.$t('ticket.tasks.uploadError') || 'Erro ao enviar anexos.' });
                    console.error(err);
                })
                .finally(() => { this.uploadingStatusAnexos = false; });
        }
    }
};
</script>

<style scoped>
.detail-tabs {
    max-width: 110%;
    position: relative;
}

.detail-tabs ::v-deep .v-slide-group__wrapper {
    overflow-x: auto;
    -webkit-overflow-scrolling: touch;
}

.detail-tabs::after {
    content: '';
    position: absolute;
    top: 0;
    right: 0;
    width: 10px;
    height: 100%;
    pointer-events: none;
    background: linear-gradient(to right, rgba(255, 255, 255, 0), rgba(255, 255, 255, 1));
}

.detail-tabs ::v-deep .v-tab {
    flex: 0 0 auto;
    min-width: max-content;
    white-space: nowrap;
}

.detail-tabs-hint {
    display: none;
    align-items: center;
    gap: 4px;
    margin-top: -12px;
    margin-bottom: 12px;
    color: #1976d2;
    font-size: 11px;
    font-weight: 500;
}

@media (max-width: 600px) {
    .detail-tabs-hint {
        display: inline-flex;
    }
}

@media (min-width: 601px) {
    .detail-tabs ::v-deep .v-slide-group__content {
        justify-content: center;
    }
}

/* Vuetify 2 has no white-space utility; DB/text fields use \n which HTML collapses without this */
.ticket-detail-corpo {
    white-space: pre-wrap;
    overflow-wrap: break-word;
    word-break: break-word;
}
</style>
